import React, { useRef, useEffect, useState, useMemo, useCallback, lazy } from 'react';
import axios from 'axios';
import { lockFromError } from './utils/lockFromError';
import { applyScopeHeader, canUseMyTerritory, cellsFromTerritories, filterToCells, readStoredToggle, storeToggle } from './utils/myTerritory';
import MobileNav from './components/MobileNav';
import { useGeofencing } from './hooks/useGeofencing';
import './App.css';

// React Map GL imports
import { Map as MapGL, Marker } from 'react-map-gl';
import DraggableMapCard from './components/DraggableMapCard';
import ResizeHandle from './components/ResizeHandle';
import 'mapbox-gl/dist/mapbox-gl.css';

import { latLngToCell, cellToBoundary, cellToLatLng } from 'h3-js';

// Deck.gl imports
import { DeckGL } from '@deck.gl/react';
import { GeoJsonLayer, IconLayer, PathLayer, TextLayer } from '@deck.gl/layers';

// Component Imports
import SideBar, { hasFilterPanel } from './components/SideBar';
import MainContent from './components/MainContent';
import ListView from './components/ListView';
import AgentBriefing from './components/AgentBriefing';
import MapLegend from './components/MapLegend';
import TodaysVisits from './components/TodaysVisits';
import LeadDetailPanel from './components/LeadDetailPanel';
import TodaysInsights from './components/TodaysInsights';
import BottomNav from './components/BottomNav';
import RejectionModal from './components/RejectionModal';
import AddVisitModal from './components/AddVisitModal';
import IntentFilter from './components/IntentFilter';
import GoogleOAuthCallback from './components/GoogleOAuthCallback';
import NavRail from './components/NavRail';
import Login from './components/Login';
import PlatformHome from './components/PlatformHome';
import API_BASE_URL from './apiConfig';
import { adoptAccountTheme } from './theme';
import Deferred from './components/Deferred';
import { debug } from './utils/log';
import { availableAreaLayers, effectiveH3View } from './utils/areaLayers';
import { datasetGeoJson, metricLabel, rampColor } from './utils/datasetLayer';
import { consumeHandoff, handoffUrl } from './utils/handoff';
import { openPaddleCheckout, takePaddleTransaction } from './utils/paddleCheckout';

// Code-split screens. Each is rendered conditionally and most users never
// open any of them, so they are fetched on first use rather than shipped in
// the bundle that renders the map. AdminHub pulls TerritoryManager and
// TerritoryCellEditor with it; TeamSettingsDashboard pulls TemplateManager
// and SettingsAnalytics. See components/Deferred.js for the boundary.
const DashboardView = lazy(() => import('./components/DashboardView'));
const ProfileView = lazy(() => import('./components/ProfileView'));
const OrgSettings = lazy(() => import('./components/OrgSettings'));
const OrgServices = lazy(() => import('./components/OrgServices'));
const AdminHub = lazy(() => import('./components/AdminHub'));
const GraphQueryPanel = lazy(() => import('./components/GraphQueryPanel'));
const TasksPanel = lazy(() => import('./components/TasksPanel'));
const AccountsPanel = lazy(() => import('./components/AccountsPanel'));
const MySettings = lazy(() => import('./components/MySettings'));
const TeamSettingsDashboard = lazy(() => import('./components/TeamSettingsDashboard'));



// Define constants
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const H3_RESOLUTION = 9;

// Blue gradient for Traffic layer (light to dark blue)
const trafficColors = [
  [187, 222, 251, 180],  // Very light blue
  [144, 202, 249, 180],  // Light blue
  [66, 165, 245, 180],   // Medium blue
  [33, 150, 243, 180],   // Blue
  [25, 118, 210, 180]    // Dark blue
];

const getColorByTrafficScore = (score) => {
  const score_bucket = Math.min(4, Math.floor(score / 2)); // 0-10 -> 0-4
  return trafficColors[score_bucket];
};

/**
 * Below this zoom the map stops drawing one pin per lead and draws cluster
 * circles instead — leads grouped into H3 resolution-6 cells (~36km across),
 * the number being how many fall inside. Without it the map would try to render
 * every marker in view at country scale.
 *
 * Shared with MapLegend so the legend explains clustering exactly when it is
 * actually happening.
 */
export const CLUSTER_ZOOM = 11;

/** Cell revenue labels only appear once cells are big enough to hold them. */
export const REVENUE_LABEL_ZOOM = 13;

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // Store user details
  const [visitPermissions, setVisitPermissions] = useState(null); // Effective visit-action perms (gates visit UI)
  const [authLoading, setAuthLoading] = useState(true); // Prevent API calls until auth is validated
  const [loggingOut, setLoggingOut] = useState(false); // Suppress Login.js flash during redirect
  const sessionCheckStarted = useRef(false); // Prevent multiple session checks

  // Enable credentials for cross-site cookies
  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  // CSRF double-submit: store unsigned token in memory, attach as header on mutations.
  // WHY in-memory: fastapi-csrf-protect sets the cookie as HttpOnly by default so
  // document.cookie cannot read it. The backend now returns the unsigned token in
  // the JSON body of GET /api/csrf-token; we store it in a module-level ref so the
  // axios interceptor can use it without a React re-render.
  const csrfTokenRef = useRef(null);

  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const method = (config.method || '').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        if (csrfTokenRef.current) {
          config.headers['X-CSRF-Token'] = csrfTokenRef.current;
        }
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  // Check for valid session cookie on mount (ONLY ONCE)
  // --- BILLING STATE ---
  // One reader for /api/billing/status, used at login and after checkout.
  const refreshBillingState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/billing/status`);
      if (!data) return null;
      const next = {
        status:          data.status,
        tier:            data.tier,
        plan:            data.plan,
        planName:        data.plan_name,
        billingStatus:   data.billing_status,
        expiresAt:       data.trial_ends_at,
        daysLeft:        data.days_left,
        graceUntil:      data.grace_until,
        hasSubscription: Boolean(data.has_subscription),
        checkoutClient:  data.checkout_client || null,
      };
      setBillingState(next);
      return next;
    } catch {
      return null; // non-fatal — billing state just stays as it was
    }
  }, []);

  // After the payment provider redirects back, the webhook may land a moment
  // later: confirm activation by polling status briefly instead of telling
  // the user something that isn't true yet.
  const handleBillingReturn = useCallback(async (billing, isAdmin) => {
    const ret = sessionStorage.getItem('billing_return');
    const openBilling = sessionStorage.getItem('open_billing') === '1';
    sessionStorage.removeItem('billing_return');
    sessionStorage.removeItem('open_billing');

    if (openBilling && isAdmin) setShowOrgServices(true);
    if (ret === 'canceled') {
      setNotification('Checkout canceled — you have not been charged.');
      setTimeout(() => setNotification(''), 5000);
      return;
    }
    if (ret !== 'success') return;

    setNotification('Payment received — activating your plan…');
    let current = billing;
    for (let i = 0; i < 10 && !(current && current.hasSubscription); i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
      // eslint-disable-next-line no-await-in-loop
      current = await refreshBillingState();
    }
    setNotification(current && current.hasSubscription
      ? `You're on ${current.planName || 'your new plan'}. Thank you!`
      : 'Payment received. Your plan will be active within a few minutes — refresh if it is not.');
    setTimeout(() => setNotification(''), 6000);
  }, [refreshBillingState]);

  useEffect(() => {
    // Prevent multiple session checks
    if (sessionCheckStarted.current) return;
    sessionCheckStarted.current = true;

    const checkSession = async () => {
      try {
        // ?handoff=<code> from Lead Concierge / the website: a one-time code
        // that replaces ?token= (no JWT in URLs). Exchanged before the
        // session check so the Authorization header is set in time.
        const handedOff = await consumeHandoff();
        if (handedOff) {
          localStorage.setItem('token', handedOff);
          setToken(handedOff);
          axios.defaults.headers.common['Authorization'] = `Bearer ${handedOff}`;
          debug('✅ Signed in from a handoff code');
        }

        // Step 0 (legacy) — if the marketing site (or LC) sent us a ?token= after login,
        // absorb it immediately so PlatformSwitcher can read it from localStorage.
        // This must happen BEFORE the /api/users/me call so the Authorization header
        // is populated (in case cookies aren't set yet for this origin).
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const urlToken = urlParams.get('token');
          if (urlToken && urlToken.split('.').length === 3) {
            localStorage.setItem('token', urlToken);
            setToken(urlToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${urlToken}`;
            // Strip the token from the URL bar — prevents copy-paste leaks.
            urlParams.delete('token');
            const cleanUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams}`
              : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
            debug('✅ JWT absorbed from ?token= URL param');
          }
          // Email verification redirect: backend sends ?verified=1 after the user
          // clicks their verification link. Store it in sessionStorage so it survives
          // the login redirect (unauthenticated users get sent to the marketing site).
          // Same for ?restored=1, sent after an admin opens the restore link
          // of an organization that was scheduled for deletion.
          if (urlParams.get('restored') === '1') {
            sessionStorage.setItem('org_restored_toast', '1');
            urlParams.delete('restored');
            const cleanUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams}`
              : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
          }
          if (urlParams.get('verified') === '1') {
            sessionStorage.setItem('email_verified_toast', '1');
            urlParams.delete('verified');
            const cleanUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams}`
              : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
          }
          // Paddle checkout: the provider sends the buyer back here with
          // ?_ptxn=<transaction>; the overlay opens once billing state loads.
          takePaddleTransaction();
          // Return from the payment provider (?billing=success|canceled) or a
          // lifecycle email link (?open=billing). Kept in sessionStorage so it
          // survives a login redirect, handled once billing state has loaded.
          const billingReturn = urlParams.get('billing');
          const openParam = urlParams.get('open');
          if (billingReturn || openParam) {
            if (billingReturn === 'success' || billingReturn === 'canceled') {
              sessionStorage.setItem('billing_return', billingReturn);
            }
            if (openParam === 'billing') sessionStorage.setItem('open_billing', '1');
            urlParams.delete('billing');
            urlParams.delete('open');
            const cleanUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams}`
              : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
          }
        } catch (_urlErr) {
          // URL parsing failure is non-fatal — fall through to cookie auth.
        }

        // Try to access protected user endpoint to verify cookie & get details.
        // This runs before the [token] effect copies the saved JWT into the
        // Authorization header, so a session with no cookie (a local /token
        // login, or a cookie the browser dropped) got 401 here and was logged
        // out on every reload. Fall back to the saved token once — not first,
        // because the backend prefers the header and a stale saved token would
        // then override a valid cookie.
        let response;
        try {
          // skipAuthLogout: a 401 here isn't the end yet (see the retry below).
          response = await axios.get(`${API_BASE_URL}/api/users/me`, { skipAuthLogout: true });
        } catch (cookieErr) {
          const saved = localStorage.getItem('token');
          if (cookieErr.response?.status !== 401 || !saved) throw cookieErr;
          response = await axios.get(`${API_BASE_URL}/api/users/me`, {
            headers: { Authorization: `Bearer ${saved}` },
          });
          axios.defaults.headers.common['Authorization'] = `Bearer ${saved}`;
        }
        setIsAuthenticated(true);
        setCurrentUser(response.data);
        adoptAccountTheme(response.data);
        setAuthLoading(false); // Auth validation complete
        debug('✅ Session validated successfully');

        // Show email-verified toast if the user just clicked their verification link.
        if (sessionStorage.getItem('email_verified_toast') === '1') {
          sessionStorage.removeItem('email_verified_toast');
          setNotification('Email verified! Your account is now active.');
        }
        if (sessionStorage.getItem('org_restored_toast') === '1') {
          sessionStorage.removeItem('org_restored_toast');
          setNotification('Organization restored. Your team can sign in again. Choose a plan in Billing to keep using NorthAI.');
        }

        // Billing state for all users — drives the trial/past-due banners and
        // the lapsed overlay.
        const billing = await refreshBillingState();
        handleBillingReturn(billing, response.data?.role === 'admin');
        openPaddleCheckout(billing?.checkoutClient, { onError: setNotification });

        // Fetch CSRF token and store in memory for the request interceptor.
        // Before the refresh below: refresh-token is a cookie-authenticated POST,
        // so without the token it was refused (403) and the browser reported it
        // as a CORS error.
        // The backend returns the unsigned token in the JSON body because the
        // signed cookie it also sets is HttpOnly (unreadable via document.cookie).
        try {
          const csrfResp = await axios.get(`${API_BASE_URL}/api/csrf-token`);
          if (csrfResp.data?.csrf_token) {
            csrfTokenRef.current = csrfResp.data.csrf_token;
            debug('✅ CSRF token stored');
          }
        } catch (csrfErr) {
          console.warn('⚠️ CSRF token fetch failed (non-fatal):', csrfErr);
        }
        // Ensure localStorage always has a fresh JWT for PlatformSwitcher.
        // If token is absent (cookie-only session — e.g. the user came back on a
        // new browser tab after localStorage was cleared) we call refresh-token to
        // mint a new one. This is the fix for "GeoAgent → Lead Concierge re-login".
        if (!localStorage.getItem('token')) {
          try {
            const refreshRes = await axios.post(`${API_BASE_URL}/api/auth/refresh-token`);
            const freshToken = refreshRes.data?.access_token;
            if (freshToken) {
              localStorage.setItem('token', freshToken);
              setToken(freshToken);
              axios.defaults.headers.common['Authorization'] = `Bearer ${freshToken}`;
              debug('✅ JWT refreshed from cookie session for cross-product handoff');
            }
          } catch (_refreshErr) {
            // Non-fatal — PlatformSwitcher will still work if the user navigates
            // to LC while the cookie is valid (LC will then show its login page,
            // but that's the pre-existing fallback behaviour).
            console.warn('⚠️ Token refresh failed (non-fatal):', _refreshErr);
          }
        }

      } catch (error) {
        console.error('❌ Session validation failed - clearing storage and redirecting');

        // CRITICAL: Stop all state updates immediately
        setAuthLoading(false);
        setIsAuthenticated(false);

        // Clear everything to prevent any further requests
        window.localStorage.clear();
        window.sessionStorage.clear();

        // Delete axios auth header
        delete axios.defaults.headers.common['Authorization'];

        // Only redirect to external marketing site when explicitly configured.
        // Without a URL (local dev), we stay on this page and show Login.js.
        const marketingUrl = process.env.REACT_APP_MARKETING_URL;
        if (marketingUrl) {
          debug(`🔄 Redirecting to: ${marketingUrl}/login`);
          window.location.replace(`${marketingUrl}/login`);
        }
      }
    };

    checkSession();
    // Both callbacks are stable (useCallback); sessionCheckStarted makes this run once.
  }, [refreshBillingState, handleBillingReturn]);

  // Guard to ensure the logout flow (local clear + API call + redirect) fires
  // at most once even when many parallel requests hit a 401 simultaneously.
  const logoutInFlightRef = useRef(false);

  const handleLogout = useCallback(async ({ skipApi = false } = {}) => {
    if (logoutInFlightRef.current) return;
    logoutInFlightRef.current = true;

    // Show neutral screen immediately — prevents Login.js from flashing
    // before the marketing-site redirect completes.
    setLoggingOut(true);
    setCurrentView('map');
    localStorage.removeItem('token');
    setToken(null);
    setIsAuthenticated(false);

    // Await logout with 2s timeout — ensures server clears the session cookie
    // before we redirect. Without this, the HttpOnly cookie remains valid on
    // the server and the session stays alive even though the client has left.
    // Skipped when we're responding to a 401 — the server has already invalidated
    // the session and POSTing /api/logout just produces more 401s in the log.
    if (!skipApi) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        await axios.post(`${API_BASE_URL}/api/logout`, {}, { signal: controller.signal });
        clearTimeout(timeoutId);
      } catch (err) {
        // Timeout or network error — proceed with redirect anyway.
        // The token was already removed from localStorage so the client is clean.
        console.warn("Logout API call failed or timed out, proceeding with redirect:", err?.message);
      }
    }

    // Redirect to marketing site login only if configured; otherwise stay on local Login.js
    const marketingUrl = process.env.REACT_APP_MARKETING_URL;
    if (marketingUrl) {
      window.location.href = `${marketingUrl}/login`;
      // loggingOut stays true — page is navigating away, no further renders needed
    } else {
      setIsAuthenticated(false);
      setLoggingOut(false); // local dev: show Login.js now, no redirect
    }
  }, []);

  // Called by Login.js after a successful local /token login.
  const handleLoginSuccess = useCallback(async (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    try {
      const meRes = await axios.get(`${API_BASE_URL}/api/users/me`);
      setCurrentUser(meRes.data);
      adoptAccountTheme(meRes.data);
      setIsAuthenticated(true);
      try {
        const csrfResp = await axios.get(`${API_BASE_URL}/api/csrf-token`);
        if (csrfResp.data?.csrf_token) csrfTokenRef.current = csrfResp.data.csrf_token;
      } catch {}
    } catch {
      localStorage.removeItem('token');
      setToken(null);
    }
  }, []);

  // Load the rep's effective visit-action permissions to gate visit-list buttons.
  // Managers/admins fetch them too (harmless) but don't see the gated salesperson UI.
  useEffect(() => {
    if (!isAuthenticated) { setVisitPermissions(null); return; }
    axios.get(`${API_BASE_URL}/api/role-settings/my-settings`)
      .then(res => setVisitPermissions(res.data?.effective_settings || null))
      .catch(() => setVisitPermissions(null));
  }, [isAuthenticated]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // DO NOT set isAuthenticated here - wait for session validation to complete
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
    }
  }, [token]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        console.error('Axios interceptor error:', error);
        if (error.response && error.response.status === 401 && !error.config?.skipAuthLogout) {
          // Session already expired on the server — skip the logout POST
          // (would just yield another 401) and let the guard in handleLogout
          // collapse parallel 401s into a single redirect.
          handleLogout({ skipApi: true });
        }
        // §23: service_not_entitled — surface a lock banner so the user
        // can upgrade or ask their admin. Non-intrusive (doesn't logout).
        if (error.response && error.response.status === 402) {
          const lock = lockFromError(error.response.data?.detail);
          if (lock) setLockedService(lock);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [handleLogout]);

  // Three-tier filtering state (MOVED HERE - must be declared before useEffect that uses it)
  const [simpleFilters, setSimpleFilters] = useState({
    primary: 'ai_picks',      // Default: AI recommendations
    entityType: null           // null = all, 'leads_only', 'customers_only'
  });

  // Visit filtering state (MOVED HERE - must be declared before useEffect that uses it)
  const [visitFilterMode, setVisitFilterMode] = useState('today'); // Filter mode: 'today' | 'overdue' | 'scheduled' — strict 3-filter contract
  const [visitViewMode, setVisitViewMode] = useState('route'); // View mode: 'route' (active visits) or 'history' (completed visits)

  // Refs that always hold the LATEST visitViewMode / visitFilterMode values.
  // WHY: The useEffect below has [simpleFilters] as a dep but NOT visitViewMode/visitFilterMode,
  // to avoid a cascading re-fetch loop. Without refs, the effect closure captures the values at
  // the time the effect was last registered — i.e. stale values — so switching to History and then
  // triggering a simpleFilters change would call fetchVisitsWithFilter(..., 'route') and wipe
  // the history list. Using refs lets us read the *current* value without adding it as a dep.
  const visitViewModeRef = useRef('route');
  const visitFilterModeRef = useRef('today');
  useEffect(() => { visitViewModeRef.current = visitViewMode; }, [visitViewMode]);
  useEffect(() => { visitFilterModeRef.current = visitFilterMode; }, [visitFilterMode]);
  const [totalVisitCount, setTotalVisitCount] = useState(0); // Total count of ALL visits (active + historical) for QuickMenu visibility
  const [handoverCount, setHandoverCount] = useState(0); // Pending handovers needing the user's attention (nav badge)
  const [taskCount, setTaskCount] = useState(0); // Open tasks assigned to the user (nav badge)
  const [tasksTab, setTasksTab] = useState('mine'); // Tab the Tasks view opens on
  const [coveringNow, setCoveringNow] = useState([]); // Active covers where this user is the coverer (map banner)
  const [activeVisitEntityIds, setActiveVisitEntityIds] = useState(new Set()); // All active visit entity IDs (planned + checked_in) for duplicate checking
  const [overdueCount, setOverdueCount] = useState(0); // Count of overdue visits for tab badge
  const [overdueList, setOverdueList] = useState([]); // Overdue visits from backend response (planned + scheduled_date < today)
  const [visitsLoading, setVisitsLoading] = useState(false); // True while a tab-switch fetch is in flight

  // Debounce timer ref for viewport-triggered re-fetches
  const viewportFetchTimer = useRef(null);
  // Track last fetched bounds to avoid redundant requests
  const lastFetchedBounds = useRef(null);
  // Monotonic id so only the most recent entity request may write allLeads.
  const latestEntityRequest = useRef(0);
  // True only when the backend capped the full result (X-Entities-Truncated).
  // Viewport loading exists for that case alone: when the whole set is loaded,
  // a viewport fetch used to REPLACE it with just what was on screen, so "All
  // Entities" showed only the visible ones until a page refresh.
  const entitiesTruncatedRef = useRef(false);
  // Visible records the API could not place on the map (X-Entities-Unplaced).
  const [unplacedCount, setUnplacedCount] = useState(0);
  // Latest fetchMapEntities, for the debounced viewport timer: the timer is set
  // in one render and fires in a later one, and must not use an old filter.
  const fetchMapEntitiesRef = useRef(null);

  // Fetch leads and customers data — extracted so it can be called imperatively after mutations
  const fetchMapEntities = useCallback(async (bbox = null) => {
    if (!isAuthenticated || authLoading) return;
    const requestId = ++latestEntityRequest.current;
    const isStale = () => requestId !== latestEntityRequest.current;
    try {
      const params = new URLSearchParams();
      if (simpleFilters.primary) params.append('primary', simpleFilters.primary);
      if (simpleFilters.entityType) params.append('entity_type', simpleFilters.entityType);
      // Viewport filter: only fetch entities in the visible map area
      if (bbox) {
        params.append('bbox_sw_lat', bbox.bbox_sw_lat);
        params.append('bbox_sw_lng', bbox.bbox_sw_lng);
        params.append('bbox_ne_lat', bbox.bbox_ne_lat);
        params.append('bbox_ne_lng', bbox.bbox_ne_lng);
      }

      const entitiesResponse = await axios.get(`${API_BASE_URL}/api/map/entities?${params}`);
      if (isStale()) return;   // a newer request has been issued — discard this reply
      if (bbox) {
        // Viewport page of a truncated set: merge it in, never replace what is
        // already loaded (the list and counts read this same array).
        setAllLeads((prev) => {
          const byId = new Map(prev.map((e) => [e.global_id, e]));
          entitiesResponse.data.forEach((e) => byId.set(e.global_id, e));
          return Array.from(byId.values());
        });
      } else {
        setAllLeads(entitiesResponse.data);
        entitiesTruncatedRef.current = entitiesResponse.headers?.['x-entities-truncated'] === 'true';
        setUnplacedCount(Number(entitiesResponse.headers?.['x-entities-unplaced']) || 0);
        lastFetchedBounds.current = null;
      }
      setEntitiesLoaded(true);
    } catch (error) {
      console.error('Failed to fetch map entities:', error);
      try {
        const [leadsResponse, customersResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/map/leads`),
          axios.get(`${API_BASE_URL}/api/map/customers`)
        ]);
        if (isStale()) return;
        setAllLeads([...leadsResponse.data, ...customersResponse.data]);
        setEntitiesLoaded(true);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, simpleFilters]);
  fetchMapEntitiesRef.current = fetchMapEntities;

  // Fetch leads and customers data when authenticated (Dual Endpoint Strategy)
  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated || authLoading) return;
      // A viewport fetch queued under the previous filter must not land after this one.
      clearTimeout(viewportFetchTimer.current);
      await fetchMapEntities();
      // Use refs so we always read the LATEST view/filter mode even though they are
      // intentionally excluded from the dep array (see note below).
      await fetchVisitsWithFilter(visitFilterModeRef.current, visitViewModeRef.current);
      await fetchActiveVisitIds();
      await fetchTotalVisitCount();
      await fetchOverdueCount();
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, simpleFilters]); // Re-fetch when auth state or entity filters change
  // NOTE: visitFilterMode and visitViewMode are intentionally excluded from the dep array.
  // Visit filter/view changes are handled explicitly by handleVisitFilterChange/handleVisitViewModeChange.
  // Including them here causes a cascading re-fetch loop (every visit action triggered full data reload).
  // We use visitViewModeRef/visitFilterModeRef (kept in sync via separate effects) so this effect
  // always reads the current values without needing them as deps.


  // --- IDLE DETECTION LOGIC ---
  /* const lastActivity = useRef(Date.now()); - Re-enable if needed for idle detection */
  const lastActivity = useRef(Date.now());

  useEffect(() => {
    const handleActivity = () => {
      if (isAuthenticated) {
        const now = Date.now();
        const oneHour = 60 * 60 * 1000;

        if (now - lastActivity.current > oneHour) {
          debug("User idle for > 1 hour. Logging out.");
          handleLogout();
        }

        lastActivity.current = now;
      }
    };

    // Listen for common user interactions
    // mousemove and scroll fire at gesture rate, so they are registered passive
    // and captured — the handler never calls preventDefault, and `scroll` does
    // not bubble, so capture is what lets one listener see nested panes.
    const passive = { passive: true };
    const passiveCapture = { passive: true, capture: true };
    window.addEventListener('click', handleActivity, passive);
    window.addEventListener('keydown', handleActivity, passive);
    window.addEventListener('mousemove', handleActivity, passive);
    window.addEventListener('scroll', handleActivity, passiveCapture);

    return () => {
      window.removeEventListener('click', handleActivity, passive);
      window.removeEventListener('keydown', handleActivity, passive);
      window.removeEventListener('mousemove', handleActivity, passive);
      window.removeEventListener('scroll', handleActivity, passiveCapture);
    };
  }, [isAuthenticated, handleLogout]);


  // Map view state
  const [viewState, setViewState] = useState({
    longitude: 139.6917,
    latitude: 35.6895,
    zoom: 12,
    pitch: 0,
    bearing: 0
  });

  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [myLocation, setMyLocation] = useState(null);

  const [allLeads, setAllLeads] = useState([]);
  // "My territory" view for an admin / HQ manager who also works a territory.
  const [myTerritory, setMyTerritory] = useState({ count: 0, cells: new Set() });
  const [myTerritoryOnly, setMyTerritoryOnly] = useState(readStoredToggle);
  // True after the first successful entity fetch — gates the admin
  // empty-org auto-redirect so it never fires on a transient empty state.
  const [entitiesLoaded, setEntitiesLoaded] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [visitList, setVisitList] = useState([]);
  const [showVisitList, setShowVisitList] = useState(false); // Start hidden
  const [showInsights, setShowInsights] = useState(false); // Start hidden
  const [showDetailedView, setShowDetailedView] = useState(false); // For detailed lead panel
  const [notification, setNotification] = useState('');
  const [selectedIntents, setSelectedIntents] = useState(['all']); // Intent filter for visit list

  // Undo state management (30-second window for visit actions)
  const [undoableActions, setUndoableActions] = useState(new Map());
  const undoTimersRef = useRef(new Map()); // Store timeout IDs for cleanup

  // Route optimization state
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [showRouteOnMap, setShowRouteOnMap] = useState(false);
  const [routeGeoJson, setRouteGeoJson] = useState(null); // Driving route geometry from MapBox Directions

  // Legacy filter (keep for backward compatibility with other components)
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  const isFocusMode = false;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [currentView, setCurrentView] = useState('map');
  const [showSettings, setShowSettings] = useState(false);
  const [showOrgServices, setShowOrgServices] = useState(false);
  // Bumped after each completed import so the Admin Hub re-fetches its
  // overview stats and import history.
  // §23: locked-service state — set when any API returns
  // 402 {code:'service_not_entitled', service_key}. Drives the upgrade banner.
  const [lockedService, setLockedService] = useState(null);

  // Billing state — fetched once after session validation (admin only).
  // Drives the trial expiry banner and the subscription-lapsed overlay.
  const [billingState, setBillingState] = useState(null); // { status, tier, expiresAt, daysLeft } | null
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const [h3Data, setH3Data] = useState([]);

  // First-login onboarding: an admin whose org has no leads/customers yet
  // has nothing useful on the map — land them on the Admin Hub instead.
  // Fires at most once per session (ref guard) so it never fights the user.
  const didAutoOpenAdminHub = useRef(false);
  useEffect(() => {
    if (didAutoOpenAdminHub.current) return;
    if (!entitiesLoaded || currentUser?.role !== 'admin') return;
    didAutoOpenAdminHub.current = true;
    if (allLeads.length === 0) {
      setCurrentView('admin');
    }
  }, [entitiesLoaded, currentUser, allLeads.length]);

  const [h3SalesFilterRange, setH3SalesFilterRange] = useState([0, 0]);
  const [h3TrafficFilterRange, setH3TrafficFilterRange] = useState([0, 10]);
  const [h3RevenueFilterRange, setH3RevenueFilterRange] = useState([0, 0]);
  const [requestedH3View, setH3View] = useState('sales');
  const [isH3Visible, setIsH3Visible] = useState(false);


  const [visibleH3Indexes, setVisibleH3Indexes] = useState(new Set());
  const [h3FiltersApplied, setH3FiltersApplied] = useState(false);
  const [isBriefingOpen, setIsBriefingOpen] = useState(false);
  const [isBriefingLoading, setIsBriefingLoading] = useState(false);
  const [fullBriefingList, setFullBriefingList] = useState([]);
  const [displayedBriefing, setDisplayedBriefing] = useState([]);
  const memoizedDisplayedBriefing = useMemo(() => displayedBriefing, [displayedBriefing]);
  const [loadingLeadId, setLoadingLeadId] = useState(null);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  // Currently-focused briefing lead (clicked from either side of the split).
  // Drives map highlight + briefing card highlight.
  const [focusedBriefingLeadId, setFocusedBriefingLeadId] = useState(null);
  const [rejectedLeadIds, setRejectedLeadIds] = useState(new Set()); // Track rejected leads
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [entityToReject, setEntityToReject] = useState(null);

  // AddVisitModal state — opened when user clicks "Add to Visit List" from LeadDetailPanel
  const [showAddVisitModal, setShowAddVisitModal] = useState(false);
  const [addVisitEntity, setAddVisitEntity] = useState(null); // the lead/customer pending add
  const [addVisitLoading, setAddVisitLoading] = useState(false); // spinner while POST is in flight

  // Resizable split layout
  const [rightPanelWidth, setRightPanelWidth] = useState(400);
  const [isFloating, setIsFloating] = useState(false);

  const handleRightPanelResize = useCallback((newWidth) => {
    setRightPanelWidth(newWidth);
    requestAnimationFrame(() => mapRef.current?.resize());
  }, []);

  const [categories, setCategories] = useState({ all: 'All Entities' }); // Dynamic categories from backend

  // Fetch dynamic categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      if (!isAuthenticated || authLoading) return;

      try {
        const response = await axios.get(`${API_BASE_URL}/api/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        // Fallback to default categories
        setCategories({
          all: 'All Entities',
          current_customers: 'Active Customers',
          at_risk_customers: 'At Risk',
          won: 'Won Deals',
          leads: 'Leads & Prospects',
          inactive: 'Inactive / Lost'
        });
      }
    };

    fetchCategories();
  }, [isAuthenticated, authLoading]);

  // Convert categories object to array of keys for SideBar
  const customerStatuses = useMemo(() => {
    return Object.keys(categories);
  }, [categories]);

  // --- Effect: Resize map when its container width changes ---
  // MapBox GL caches the canvas dimensions on its first load. When the
  // container shrinks (briefing/visits split opens) or grows (split closes,
  // focus mode toggles) we must call .resize() explicitly; otherwise the map
  // renders at the old size, which looks like it's "locked" and zooms
  // strangely around a stale center.
  useEffect(() => {
    if (!mapRef.current) return;
    // Run twice: immediately after the CSS transition starts, and again
    // after it completes, so the canvas matches the final container size.
    const t1 = setTimeout(() => mapRef.current?.resize(), 50);
    const t2 = setTimeout(() => mapRef.current?.resize(), 350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isFocusMode, isBriefingOpen, showVisitList, currentView, sidebarCollapsed]);

  // --- Effect: ResizeObserver on the map container ---
  // Catches any container size change (sidebar collapse, panel toggle, window resize)
  // that the state-based effect above might miss. Fires after the browser has
  // committed the new layout dimensions, so mapRef.current.resize() reads correctly.
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      mapRef.current?.resize();
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Escape-to-close for the right-split panels (AI Briefing, Visits).
  // Only closes the outermost overlay; lower-level modals (RejectionModal,
  // LeadDetailPanel) handle their own Escape.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (showDetailedView) return; // detail panel owns Escape first
      if (isBriefingOpen) {
        setIsBriefingOpen(false);
        return;
      }
      if (currentView === 'visits') {
        setCurrentView('map');
        setShowVisitList(false);
        return;
      }
      if (showVisitList) {
        setShowVisitList(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isBriefingOpen, showVisitList, currentView, showDetailedView]);

  // --- DEBUG: Monitor visit panel visibility ---
  // Commented out to reduce console spam
  // useEffect(() => {
  //   const shouldShowVisitPanel = currentView === 'map' && showVisitList && visitList.length > 0 && !showDetailedView && !isBriefingOpen;
  //   debug('📊 Visit Panel Visibility Check:', {
  //     shouldShow: shouldShowVisitPanel,
  //     currentView,
  //     showVisitList,
  //     visitListLength: visitList.length,
  //     showDetailedView,
  //     isBriefingOpen
  //   });
  // }, [currentView, showVisitList, visitList.length, showDetailedView, isBriefingOpen]);

  // --- UTILITY HANDLERS ---
  const showNotification = useCallback((message, duration = 3000) => {
    setNotification(message);
    setTimeout(() => setNotification(''), duration);
  }, []);


  // Fetch detailed entity information (wrapped in useCallback for stable reference)
  // NOTE: This function fetches data but does NOT open the panel - caller must set showDetailedView
  const fetchDetailedEntity = useCallback(async (globalId, shouldOpenPanel = false) => {
    if (!globalId) return;

    setIsLoadingDetails(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/entities/${globalId}`);
      setSelectedLead(response.data);

      // Only open panel if explicitly requested (e.g., from "View Full Details" button)
      if (shouldOpenPanel) {
        setIsMobileMenuOpen(false);
        setShowDetailedView(true);
      }
    } catch (error) {
      console.error('Failed to fetch entity details:', error);
      showNotification('Failed to load entity details. Please try again.', 5000);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [showNotification]); // Only recreate if showNotification changes

  // Stable fetch helpers — useCallback with [] so they never cause dep-array churn
  const fetchTotalVisitCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/visits/count`);
      const totalCount = response.data.count;
      setTotalVisitCount(totalCount);
      return totalCount;
    } catch (error) {
      console.error('Failed to fetch total visit count:', error);
      return 0;
    }
  }, []);

  const fetchVisitsWithFilter = useCallback(async (filterMode, viewMode) => {
    try {
      let visits = [];

      if (viewMode === 'route') {
        // My Route: Single call returns { route, overdue, total_route, total_overdue }
        // Backend splits planned+checked_in (route) from overdue (planned+scheduled_date<today)
        const response = await axios.get(`${API_BASE_URL}/api/visits/my-route?filter_mode=${filterMode}`);
        const data = response.data;

        // Handle both new shape { route, overdue } and legacy flat array (backwards compat)
        if (data && typeof data === 'object' && !Array.isArray(data) && data.route !== undefined) {
          visits = data.route;
          setOverdueList(data.overdue || []);
          setOverdueCount(data.total_overdue || 0);
        } else {
          // Legacy flat array response
          visits = Array.isArray(data) ? data : [];
          setOverdueList([]);
        }
      } else if (viewMode === 'history') {
        // History: flat list of completed/skipped/cancelled visits, newest first.
        // Uses dedicated endpoint — my-route only returns planned/checked_in visits.
        const response = await axios.get(`${API_BASE_URL}/api/visits/history`);
        visits = Array.isArray(response.data) ? response.data : [];
      }

      setVisitList(visits);
      debug(`✅ Loaded ${visits.length} visits (view: ${viewMode}, filter: ${filterMode})`);
      return visits;
    } catch (error) {
      console.error('Failed to fetch visits:', error);
      throw error;
    }
  }, []);

  // Core API call — used by both the modal confirmation path and the briefing accept path.
  // scheduledDate: YYYY-MM-DD string (local timezone). customerConfirmed: bool.
  const executeAddToVisitList = useCallback(async (leadToAdd, visitReason = 'manual_planning', scheduledDate = null, customerConfirmed = false, scheduledTime = null, scheduledEndTime = null) => {
    // Ensure entity has global_id (for items from briefing that might not have it)
    const entityToAdd = leadToAdd.global_id
      ? leadToAdd
      : { ...leadToAdd, global_id: `lead_${leadToAdd.id}` };

    // Check if already in active visits (uses full set, not filtered view)
    if (activeVisitEntityIds.has(entityToAdd.global_id)) {
      showNotification(`${entityToAdd.name} is already in your visit list.`, 3000);
      return;
    }

    // Resolve the scheduled date — caller may pass one (modal) or we fall back to today
    const today = new Date();
    const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const resolvedDate = scheduledDate || localToday;

    // Add to visits using new API
    try {
      // Optimistically update active IDs so button changes immediately
      setActiveVisitEntityIds(prev => new Set([...prev, entityToAdd.global_id]));

      // Clear optimized route (new visit invalidates it)
      setOptimizedRoute(null);
      setRouteGeoJson(null);
      setShowRouteOnMap(false);

      await axios.post(`${API_BASE_URL}/api/visits`, {
        entity_global_id: entityToAdd.global_id,
        scheduled_date: resolvedDate,
        scheduled_time: scheduledTime || null,
        scheduled_end_time: scheduledEndTime || null,
        visit_reason: visitReason,
        customer_confirmed: customerConfirmed,
      });

      // Switch to the filter that will show the new visit:
      // - If scheduled for today → "today" filter
      // - If scheduled for a future date → "scheduled" filter
      const targetFilter = resolvedDate === localToday ? 'today' : 'scheduled';
      const targetView = 'route';
      if (targetFilter !== visitFilterMode) setVisitFilterMode(targetFilter);
      if (targetView !== visitViewMode) setVisitViewMode(targetView);

      // Re-fetch visits with the target filter (ensures new visit is visible)
      await fetchVisitsWithFilter(targetFilter, targetView);

      // Optimistically update lead status to 'planned' so pin/badge change immediately
      if (entityToAdd.global_id?.startsWith('lead_')) {
        setAllLeads(prev => prev.map(e =>
          e.global_id === entityToAdd.global_id ? { ...e, status: 'planned' } : e
        ));
      }

      // Update total visit count for QuickMenu visibility
      await fetchTotalVisitCount();

      showNotification(`${entityToAdd.name} added to visit list!`);
    } catch (error) {
      // Revert optimistic update
      setActiveVisitEntityIds(prev => {
        const next = new Set(prev);
        next.delete(entityToAdd.global_id);
        return next;
      });
      console.error('Failed to add to visits:', error);
      // Handle duplicate visit error (409 Conflict)
      if (error.response?.status === 409) {
        // Refresh active IDs from server (our local set was out of sync)
        await fetchActiveVisitIds();
        showNotification(`${entityToAdd.name} is already in your visit list.`, 3000);
      } else {
        showNotification('Failed to add to visit list. Please try again.', 3000);
      }
    }
  }, [showNotification, activeVisitEntityIds, visitFilterMode, visitViewMode, fetchVisitsWithFilter, fetchTotalVisitCount]);

  // Manual add path (from LeadDetailPanel "Add to Visit List" button) — opens the modal
  // so the user can pick a date and optionally mark as customer-confirmed.
  // Briefing accept path bypasses this and calls executeAddToVisitList directly.
  const handleAddToVisitList = useCallback((leadToAdd, visitReason = 'manual_planning') => {
    const entityToAdd = leadToAdd.global_id
      ? leadToAdd
      : { ...leadToAdd, global_id: `lead_${leadToAdd.id}` };

    // Gate: already in list — no point opening the modal
    if (activeVisitEntityIds.has(entityToAdd.global_id)) {
      showNotification(`${entityToAdd.name} is already in your visit list.`, 3000);
      return;
    }

    // Store the entity + reason so the modal confirm handler can use them
    setAddVisitEntity({ ...entityToAdd, _visitReason: visitReason });
    setShowAddVisitModal(true);
  }, [activeVisitEntityIds, showNotification]);

  const isLeadInVisitList = (globalId) => activeVisitEntityIds.has(globalId);

  const handleRemoveFromVisitList = async (globalId) => {
    // Find the visit to remove
    const visitToRemove = visitList.find(item => item.entity_global_id === globalId);
    if (!visitToRemove) return;

    // Store visit data for undo (need to re-create the visit)
    // Note: visit_state is NOT included because API always creates visits as 'planned'
    const visitData = {
      entity_global_id: visitToRemove.entity_global_id,
      scheduled_date: visitToRemove.scheduled_date,
      visit_reason: visitToRemove.visit_reason,
      notes: visitToRemove.notes
    };

    // Remove visit using new API
    try {
      await axios.delete(`${API_BASE_URL}/api/visits/${visitToRemove.id}`);

      // Update local state
      setOptimizedRoute(null);
      setRouteGeoJson(null);
      setShowRouteOnMap(false);
      setVisitList(prevList => prevList.filter(item => item.entity_global_id !== globalId));
      setActiveVisitEntityIds(prev => {
        const next = new Set(prev);
        next.delete(globalId);
        return next;
      });

      // Update total visit count for QuickMenu visibility
      await fetchTotalVisitCount();

      showNotification(`${visitToRemove.entity_name} removed from visit list.`);

      // Register undo action (re-add the visit)
      registerUndoableAction(visitToRemove.id, 'remove', visitData, async () => {
        debug('🔄 Undoing remove - Posting visit data:', visitData);
        await axios.post(`${API_BASE_URL}/api/visits`, visitData);
        await fetchVisitsWithFilter(visitFilterMode, visitViewMode);
        await fetchActiveVisitIds();
        await fetchTotalVisitCount();
        showNotification(`${visitToRemove.entity_name} restored to visit list`);
      });
    } catch (error) {
      console.error('Failed to remove visit:', error);
      showNotification('Failed to remove from visit list. Please try again.', 3000);
    }
  };

  const handleClearVisitList = async (filterMode) => {
    if (visitList.length === 0) return;

    // Use the passed filterMode or fall back to current state
    const activeFilter = filterMode || visitFilterMode || 'all';

    try {
      // Clear visits scoped to the current filter tab
      await axios.delete(`${API_BASE_URL}/api/visits/clear-all`, {
        params: { filter_mode: activeFilter }
      });

      // Clear optimized route (no longer valid)
      setOptimizedRoute(null);
      setRouteGeoJson(null);
      setShowRouteOnMap(false);

      // Re-fetch actual state from backend
      await fetchVisitsWithFilter(visitFilterMode, visitViewMode);
      await fetchActiveVisitIds();
      await fetchTotalVisitCount();
      await fetchOverdueCount(); // Refresh overdue badge after clearing

      const filterLabels = {
        today: "Today's", overdue: 'Overdue', scheduled: 'Scheduled',
      };
      showNotification(`${filterLabels[activeFilter] || "Today's"} visits cleared!`);
    } catch (error) {
      console.error('Failed to clear visits:', error);
      showNotification('Failed to clear visit list. Please try again.', 3000);
    }
  };

  // Route optimization handler
  const handleOptimizeRoute = async () => {
    // Auto-fetch GPS if not already available
    let location = myLocation;
    if (!location) {
      if (!navigator.geolocation) {
        showNotification('Geolocation is not supported by your browser.', 5000);
        return;
      }
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000,
          });
        });
        location = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };
        setMyLocation(location);
      } catch (geoError) {
        showNotification('Could not get your location. Please enable GPS and try again.', 5000);
        return;
      }
    }

    const activeVisits = visitList.filter(v => v.visit_state === 'planned' || v.visit_state === 'checked_in');
    if (activeVisits.length < 2) {
      showNotification('Need at least 2 visits to optimize route.', 3000);
      return;
    }

    setIsOptimizing(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/visits/optimize-route`, {
        user_lat: location.latitude,
        user_lng: location.longitude,
        filter_mode: visitFilterMode
      });

      const routeData = response.data;
      setOptimizedRoute(routeData);
      setShowRouteOnMap(true);

      // Reorder visitList to match optimized order
      const orderMap = new Map();
      routeData.optimized_visit_ids.forEach((id, index) => {
        orderMap.set(id, index);
      });
      setVisitList(prev => {
        const sorted = [...prev].sort((a, b) => {
          const orderA = orderMap.get(a.id) ?? 999;
          const orderB = orderMap.get(b.id) ?? 999;
          return orderA - orderB;
        });
        return sorted;
      });

      // Fetch actual driving route from MapBox Directions API
      try {
        const coords = [];
        // Start with user location
        coords.push(`${location.longitude},${location.latitude}`);
        // Add each stop in optimized order
        routeData.legs.forEach(leg => {
          coords.push(`${leg.to_lng},${leg.to_lat}`);
        });

        // Mapbox Directions API supports max 25 coordinates per request
        if (coords.length > 25) {
          console.warn(`Route has ${coords.length} coords (max 25). Chunking requests.`);
          // Split into overlapping chunks of 25, stitch geometries together
          const allCoordinates = [];
          for (let i = 0; i < coords.length; i += 24) {
            const chunk = coords.slice(i, Math.min(i + 25, coords.length));
            if (chunk.length < 2) break;
            const chunkStr = chunk.join(';');
            const chunkUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${chunkStr}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
            const chunkResp = await fetch(chunkUrl);
            const chunkData = await chunkResp.json();
            if (chunkData.code !== 'Ok' || !chunkData.routes?.[0]?.geometry) {
              console.warn('MapBox chunk failed:', chunkData.code, chunkData.message);
              break;
            }
            const chunkCoords = chunkData.routes[0].geometry.coordinates;
            // Skip first point of subsequent chunks (overlap with previous)
            allCoordinates.push(...(allCoordinates.length > 0 ? chunkCoords.slice(1) : chunkCoords));
          }
          if (allCoordinates.length > 0) {
            setRouteGeoJson({ type: 'LineString', coordinates: allCoordinates });
          } else {
            setRouteGeoJson(null);
          }
        } else {
          const coordStr = coords.join(';');
          const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordStr}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
          const dirResponse = await fetch(directionsUrl);
          const dirData = await dirResponse.json();
          if (dirData.code === 'Ok' && dirData.routes?.[0]?.geometry) {
            setRouteGeoJson(dirData.routes[0].geometry);
          } else {
            console.warn('MapBox Directions API error:', dirData.code, dirData.message);
            setRouteGeoJson(null);
          }
        }
      } catch (dirError) {
        console.error('MapBox Directions failed, falling back to straight lines:', dirError);
        setRouteGeoJson(null);
      }

      const saved = routeData.distance_saved_km;
      if (saved > 0) {
        showNotification(`Route optimized! Saving ${saved.toFixed(1)}km`);
      } else {
        showNotification('Route optimized!');
      }
    } catch (error) {
      console.error('Route optimization failed:', error);
      const detail = error.response?.data?.detail || 'Please try again.';
      showNotification(`Failed to optimize route: ${detail}`, 4000);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Undo helper functions (30-second window)
  const registerUndoableAction = useCallback((visitId, actionType, previousState, undoCallback) => {
    // Clear any existing undo timer for this visit
    const existingTimer = undoTimersRef.current.get(visitId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set up 30-second auto-expire timer
    const timerId = setTimeout(() => {
      setUndoableActions(prev => {
        const newMap = new Map(prev);
        newMap.delete(visitId);
        return newMap;
      });
      undoTimersRef.current.delete(visitId);
    }, 30000); // 30 seconds

    // Store timer reference
    undoTimersRef.current.set(visitId, timerId);

    // Register the undoable action
    setUndoableActions(prev => {
      const newMap = new Map(prev);
      newMap.set(visitId, {
        actionType,
        previousState,
        undoCallback,
        timestamp: Date.now()
      });
      return newMap;
    });
  }, []);

  const handleUndo = useCallback(async (visitId) => {
    const action = undoableActions.get(visitId);
    if (!action) return;

    // Clear the timer
    const timerId = undoTimersRef.current.get(visitId);
    if (timerId) {
      clearTimeout(timerId);
      undoTimersRef.current.delete(visitId);
    }

    // Remove from undoable actions
    setUndoableActions(prev => {
      const newMap = new Map(prev);
      newMap.delete(visitId);
      return newMap;
    });

    // Execute the undo callback
    try {
      debug(`🔄 Executing undo for action: ${action.actionType}, visitId: ${visitId}`);
      await action.undoCallback();
    } catch (error) {
      console.error('❌ Undo failed:', error);
      console.error('Error details:', error.response?.data);
      showNotification('Failed to undo action. Please try again.', 3000);
    }
  }, [undoableActions, showNotification]);

  useEffect(() => {
    const currentTimers = undoTimersRef.current;
    return () => {
      currentTimers.forEach(timerId => clearTimeout(timerId));
    };
  }, []);

  // Check-in to a visit
  const handleCheckIn = async (visitId) => {
    // Store previous state for undo
    const visit = visitList.find(v => v.id === visitId);
    const previousState = visit?.visit_state || 'planned';

    try {
      await axios.patch(`${API_BASE_URL}/api/visits/${visitId}/check-in`, {
        // GPS coordinates will be added by backend if available
      });

      // Refresh visit list using current filter mode and view mode
      await fetchVisitsWithFilter(visitFilterMode, visitViewMode);

      // Update total visit count (check-in doesn't change total, but ensures sync)
      await fetchTotalVisitCount();

      showNotification('Checked in successfully!');

      // Register undo action (revert to previous state)
      registerUndoableAction(visitId, 'check_in', previousState, async () => {
        await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
          visit_state: previousState
        });
        await fetchVisitsWithFilter(visitFilterMode, visitViewMode);
        await fetchTotalVisitCount();
        showNotification('Check-in undone');
      });
    } catch (error) {
      console.error('Failed to check in:', error);
      showNotification('Failed to check in. Please try again.', 3000);
    }
  };

  // Geofence auto-check-in — called by useGeofencing when rep enters visit radius
  const handleGeofenceCheckIn = useCallback(async (visit, gpsCoords) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/visits/${visit.id}/check-in`, {
        check_in_lat:    gpsCoords.latitude,
        check_in_lng:    gpsCoords.longitude,
        check_in_method: 'geofence',
      });
      await fetchVisitsWithFilter(visitFilterMode, visitViewMode);
      await fetchTotalVisitCount();
      showNotification(`📍 Auto-checked in: ${visit.entity_name || 'visit'}`, 4000);
    } catch (err) {
      console.error('Geofence auto-check-in failed:', err);
    }
  }, [fetchVisitsWithFilter, visitFilterMode, visitViewMode, fetchTotalVisitCount, showNotification]);

  const { isWatching: geofenceActive } = useGeofencing(visitList, handleGeofenceCheckIn);

  // Complete a visit
  const handleCompleteVisit = async (visitId) => {
    // Store previous state for undo
    const visit = visitList.find(v => v.id === visitId);
    const previousState = visit?.visit_state || 'checked_in';

    try {
      await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
        visit_state: 'completed'
      });

      // Refresh visit list using current filter mode and view mode
      await fetchVisitsWithFilter(visitFilterMode, visitViewMode);

      // Optimistically update lead status to 'contacted' so pin/badge change immediately
      const completedVisit = visitList.find(v => v.id === visitId);
      if (completedVisit?.entity_global_id?.startsWith('lead_')) {
        setAllLeads(prev => prev.map(e =>
          e.global_id === completedVisit.entity_global_id ? { ...e, status: 'contacted' } : e
        ));
      }

      // Update total visit count (completing doesn't change total, but ensures sync)
      await fetchTotalVisitCount();

      showNotification('Visit completed! 🎉');

      // Register undo action (revert to previous state)
      registerUndoableAction(visitId, 'complete', previousState, async () => {
        await axios.patch(`${API_BASE_URL}/api/visits/${visitId}`, {
          visit_state: previousState
        });
        await fetchVisitsWithFilter(visitFilterMode, visitViewMode);
        await fetchTotalVisitCount();
        showNotification('Completion undone');
      });
    } catch (error) {
      console.error('Failed to complete visit:', error);
      showNotification('Failed to complete visit. Please try again.', 3000);
    }
  };

  // Handle visit filter mode change (date filter tabs: Today / Overdue / Scheduled)
  // Route filters always imply route view mode — handle the mode switch here so the
  // SideBar doesn't need to fire a concurrent onVisitViewModeChange call (race condition fix).
  const handleVisitFilterChange = async (newFilterMode) => {
    const targetViewMode = 'route'; // Route filter tabs always mean route mode
    setVisitFilterMode(newFilterMode);
    if (visitViewMode !== 'route') setVisitViewMode('route');
    setVisitList([]);
    setVisitsLoading(true);
    try {
      await fetchVisitsWithFilter(newFilterMode, targetViewMode);
    } finally {
      setVisitsLoading(false);
    }
  };

  // Handle visit view mode change (My Route ↔ History)
  const handleVisitViewModeChange = async (newViewMode) => {
    setVisitViewMode(newViewMode);
    // Same instant-clear pattern — also wipe the overdue list so it doesn't bleed through.
    setVisitList([]);
    setOverdueList([]);
    setVisitsLoading(true);
    try {
      await fetchVisitsWithFilter(visitFilterMode, newViewMode);
    } finally {
      setVisitsLoading(false);
    }
  };

  // Reschedule an overdue visit to a new date
  const handleRescheduleVisit = async (visitId, payload) => {
    try {
      // payload is either a plain date string (ad-hoc inline picker) or the
      // full AddVisitModal object {scheduled_date, scheduled_time, scheduled_end_time, customer_confirmed}
      // used when rescheduling a customer-confirmed appointment.
      const body = typeof payload === 'string'
        ? { new_date: payload }
        : {
          new_date: payload.scheduled_date,
          new_time: payload.scheduled_time,
          new_end_time: payload.scheduled_end_time,
          customer_confirmed: payload.customer_confirmed,
        };

      await axios.post(`${API_BASE_URL}/api/visits/${visitId}/reschedule`, body);

      // Determine which tab the rescheduled visit will land in
      const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
      const targetFilter = body.new_date === todayStr ? 'today' : 'scheduled';

      // Switch to that tab so the user sees the rescheduled visit immediately
      setVisitFilterMode(targetFilter);
      await fetchVisitsWithFilter(targetFilter, visitViewMode);
      // fetchVisitsWithFilter already updates overdueCount via total_overdue in the response

      showNotification(`Visit rescheduled — moved to ${targetFilter === 'today' ? 'Today' : 'Scheduled'} tab`);
    } catch (error) {
      console.error('Failed to reschedule visit:', error);
      const msg = error.response?.data?.detail || 'Failed to reschedule visit.';
      showNotification(msg, 3000);
    }
  };

  // Fetch overdue visit count for badge display
  const fetchOverdueCount = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/visits/my-route?filter_mode=overdue`);
      const data = response.data;
      // Endpoint returns { route: [], overdue: [...], total_route: 0, total_overdue: N }
      const count = (data && typeof data === 'object' && !Array.isArray(data))
        ? (data.total_overdue ?? data.overdue?.length ?? 0)
        : (Array.isArray(data) ? data.length : 0); // backwards compat guard
      setOverdueCount(count);
    } catch (error) {
      console.error('Failed to fetch overdue count:', error);
    }
  };

  // Fetch all active visit entity IDs for duplicate checking (independent of current filter)
  const fetchActiveVisitIds = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/visits/active-ids`);
      setActiveVisitEntityIds(new Set(response.data.entity_ids));
    } catch (error) {
      console.error('Failed to fetch active visit IDs:', error);
    }
  };

  const handleCustomerTypeChange = (filterType) => {
    setCustomerTypeFilter(filterType);
    setSelectedLead(null);
  };

  const handleIntentChange = (intents) => {
    setSelectedIntents(intents);
  };

  const handleViewChange = (viewType) => {
    setCurrentView(viewType);
    if (viewType === 'tasks') setTasksTab('mine');
    setSelectedLead(null);

    // Settings screens are full pages on a phone, sitting above the bottom nav
    // so there is always a way out. Navigating away has to actually close them,
    // otherwise the nav is visible but inert and the page becomes a dead end.
    setShowSettings(false);

    // The list is not viewport-scoped, but the data behind it is: panning the
    // map refetches entities limited to the map's bbox, and the list renders
    // whatever that left in allLeads. Arriving from a zoomed-in map could show
    // a handful of rows, or none. Re-fetch unbounded when entering the list so
    // it shows everything matching the current entity filter.
    if (viewType === 'list') {
      lastFetchedBounds.current = null;
      fetchMapEntities().catch(err => console.error('Failed to refresh list entities:', err));
    }

    if (viewType === 'visits') {
      // Visits takes the right panel — close briefing so it doesn't win the && guard.
      // Map stays visible on the left (currentView === 'visits' keeps display: 'block').
      setIsBriefingOpen(false);
      // Silently refresh visits data every time the panel opens so history/route is
      // never stale from a previous session. Uses refs to read the latest mode values
      // without needing them as closure deps.
      fetchVisitsWithFilter(visitFilterModeRef.current, visitViewModeRef.current)
        .catch(err => console.error('Failed to refresh visits on navigation:', err));
    } else if (viewType === 'map') {
      // Back to plain map — collapse both side panels.
      setShowVisitList(false);
      setIsBriefingOpen(false);
    } else if (viewType === 'list' || viewType === 'dashboard' || viewType === 'graph' || viewType === 'admin' || viewType === 'tasks' || viewType === 'accounts' || viewType === 'my-settings' || viewType === 'team-settings') {
      // These views are full-width; close all overlays so the map doesn't obscure them.
      setShowVisitList(false);
      setIsBriefingOpen(false);
      setShowDetailedView(false);
    }
  };

  // handleShowMyLocation
  const handleShowMyLocation = () => {
    if (!navigator.geolocation) {
      showNotification("Geolocation is not supported by your browser.", 5000);
      return;
    }

    // Safari requires explicit options for reliable geolocation
    const geoOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 0 // Don't use cached position
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
        };
        setMyLocation(userLocation);
        setViewState(prev => ({
          ...prev,
          ...userLocation,
          zoom: 15
        }));
        showNotification("Showing your location!");
      },
      (error) => {
        // If permission is denied, do not retry. It will just fail again.
        if (error.code === error.PERMISSION_DENIED) {
          showNotification("Location access denied. Please check Safari settings AND macOS System Settings > Privacy > Location Services.", 7000);
          return;
        }

        console.warn('High accuracy geolocation failed, retrying with low accuracy...', error);

        // Fallback: Try again with low accuracy (Wi-Fi/IP) for Timeout or Unavailable errors
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              longitude: position.coords.longitude,
              latitude: position.coords.latitude,
            };
            setMyLocation(userLocation);
            setViewState(prev => ({
              ...prev,
              ...userLocation,
              zoom: 15
            }));
            showNotification("Showing your location!");
          },
          (fallbackError) => {
            console.error('Geolocation fallback error:', fallbackError);
            let errorMessage = "Could not get your location.";

            switch (fallbackError.code) {
              case fallbackError.PERMISSION_DENIED:
                errorMessage = "Location permission denied. Please enable location access in your browser settings.";
                break;
              case fallbackError.POSITION_UNAVAILABLE:
                errorMessage = "Location information is unavailable.";
                break;
              case fallbackError.TIMEOUT:
                errorMessage = "Location request timed out. Please try again.";
                break;
              default:
                errorMessage = `Location error: ${fallbackError.message}`;
            }

            showNotification(errorMessage, 5000);
          },
          {
            enableHighAccuracy: false, // Low accuracy fallback
            timeout: 10000,
            maximumAge: 0
          }
        );
      },
      geoOptions
    );
  };

  // handleResetH3Filters
  const handleResetH3Filters = () => {
    setH3SalesFilterRange([0, maxSales]);
    setH3TrafficFilterRange([0, maxTraffic]);
    setH3RevenueFilterRange([0, maxRevenue]);
    setH3FiltersApplied(false);
    setIsH3Visible(false);
    if (h3Data) {
      const allIndexes = new Set(h3Data.map(f => f.properties.h3_index));
      setVisibleH3Indexes(allIndexes);
    }
    showNotification('H3 filters reset!');
  };
  // --- END UTILITY HANDLERS ---


  // Load H3 territory data.
  // Refetched on tab focus, not just at login: an admin can redraw a rep's
  // territory mid-session, and without this the rep keeps the boundary they
  // logged in with until they reload the page.
  const fetchH3Data = useCallback(() => {
    return axios.get(`${API_BASE_URL}/api/h3-territory-data`)
      .then((h3Response) => {
        const fetchedH3Data = h3Response.data;
        setH3Data(fetchedH3Data.features);
        setVisibleH3Indexes(new Set((fetchedH3Data.features || []).map(f => f.properties && f.properties.h3_index)));
      })
      .catch(error => console.error("Error fetching H3 data:", error));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    fetchH3Data();

    const onFocus = () => {
      if (document.visibilityState === 'visible') fetchH3Data();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuthenticated, authLoading, fetchH3Data]);

  // Open tasks and pending store handovers. Drive the Tasks nav badge.
  // Refetched on focus for the same reason as the territory above: the work
  // is given by someone else.
  const fetchHandoverCount = useCallback(() => {
    const handovers = axios.get(`${API_BASE_URL}/api/handovers`)
      .then(({ data }) => {
        const c = data.counts || {};
        const managerOverdue = (data.handovers || []).filter(h => !h.direction && h.overdue).length;
        setHandoverCount((c.outgoing || 0) + (c.incoming || 0) + managerOverdue);
      })
      .catch(error => console.error("Error fetching handovers:", error));
    const tasks = axios.get(`${API_BASE_URL}/api/tasks/counts`)
      .then(({ data }) => setTaskCount(data.mine_active || 0))
      .catch(error => console.error("Error fetching task counts:", error));
    const covers = axios.get(`${API_BASE_URL}/api/covers`, { params: { scope: 'mine' } })
      .then(({ data }) => setCoveringNow((data.covers || []).filter(c => c.role === 'coverer' && c.status === 'active')))
      .catch(error => console.error("Error fetching cover:", error));
    return Promise.all([handovers, tasks, covers]);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    fetchHandoverCount();

    // Push notifications link to /?view=tasks, /?view=handovers and /?view=cover.
    const params = new URLSearchParams(window.location.search);
    const linkedView = params.get('view');
    if (['handovers', 'tasks', 'cover'].includes(linkedView)) {
      setTasksTab(linkedView === 'tasks' ? 'mine' : linkedView);
      setCurrentView('tasks');
      params.delete('view');
      const cleanUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }

    const onFocus = () => {
      if (document.visibilityState === 'visible') fetchHandoverCount();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuthenticated, authLoading, fetchHandoverCount]);

  // A store reported closed (rule R6) leaves the map at once.
  useEffect(() => {
    const onStoreChanged = () => {
      lastFetchedBounds.current = null;
      fetchMapEntities().catch(err => console.error('Failed to refresh after store change:', err));
    };
    window.addEventListener('northai:store-changed', onStoreChanged);
    return () => window.removeEventListener('northai:store-changed', onStoreChanged);
  }, [fetchMapEntities]);

  // --- H3 FILTERING & SCALING LOGIC ---
  const { maxSales, maxTraffic, maxRevenue, maxPopulation } = useMemo(() => {
    if (!h3Data || h3Data.length === 0) {
      return { maxSales: 5000000, maxTraffic: 10, maxRevenue: 5000000, maxPopulation: 20000 };
    }
    const sales = h3Data.map(f => (f.properties && f.properties.total_actual_sales) || 0);
    const traffic = h3Data.map(f => (f.properties && f.properties.average_traffic_score) || 0);
    const revenue = h3Data.map(f => (f.properties && f.properties.transaction_revenue) || 0);
    const population = h3Data.map(f => (f.properties && f.properties.population_density) || 0);
    return {
      maxSales: Math.max(...sales) > 0 ? Math.ceil(Math.max(...sales) / 100000) * 100000 : 5000000,
      maxTraffic: Math.max(...traffic) > 0 ? Math.ceil(Math.max(...traffic)) : 10,
      maxRevenue: Math.max(...revenue) > 0 ? Math.ceil(Math.max(...revenue) / 100000) * 100000 : 5000000,
      maxPopulation: Math.max(...population) > 0 ? Math.ceil(Math.max(...population) / 1000) * 1000 : 20000,
    };
  }, [h3Data]);

  // Traffic/population layers are offered only when some cell has real data;
  // a remembered choice of a layer without data shows sales instead.
  const areaLayers = useMemo(() => availableAreaLayers(h3Data), [h3Data]);
  const h3View = effectiveH3View(requestedH3View, areaLayers);

  // Org-imported area datasets (Admin Hub → Data → Area Data). One metric at a
  // time renders as its own layer under the markers; it is independent of the
  // sales/revenue H3 layer above.
  const [areaDatasets, setAreaDatasets] = useState([]);
  const [datasetLayer, setDatasetLayer] = useState(null); // { datasetId, key } | null
  const [datasetGeo, setDatasetGeo] = useState(null);
  const [datasetMetric, setDatasetMetric] = useState(null);

  const fetchAreaDatasets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/datasets`);
      const ready = (res.data || []).filter(d => d.status === 'ready');
      setAreaDatasets(ready);
      // Drop a selection whose dataset was deleted.
      setDatasetLayer(sel => (sel && ready.some(d => d.id === sel.datasetId && d.metrics.some(m => m.key === sel.key)) ? sel : null));
    } catch (err) {
      console.error('Loading area datasets failed', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchAreaDatasets();
  }, [isAuthenticated, fetchAreaDatasets]);

  useEffect(() => {
    if (!datasetLayer) { setDatasetGeo(null); setDatasetMetric(null); return undefined; }
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/datasets/${datasetLayer.datasetId}/values`, { params: { metric: datasetLayer.key } })
      .then(res => {
        if (cancelled) return;
        setDatasetGeo(datasetGeoJson(res.data.cells));
        setDatasetMetric(res.data.metric);
      })
      .catch(err => {
        console.error('Loading area dataset values failed', err);
        if (!cancelled) { setDatasetLayer(null); }
      });
    return () => { cancelled = true; };
  }, [datasetLayer]);

  const handleDatasetLayerChange = useCallback((datasetId, key) => {
    setDatasetLayer(sel => (sel && sel.datasetId === datasetId && sel.key === key ? null : { datasetId, key }));
  }, []);

  useEffect(() => {
    setH3SalesFilterRange([0, maxSales]);
    setH3TrafficFilterRange([0, maxTraffic]);
    setH3RevenueFilterRange([0, maxRevenue]);
  }, [maxSales, maxTraffic, maxRevenue]);


  // --- DECK.GL LAYER DEFINITION ---
  const filteredH3Data = useMemo(() => {
    if (!h3Data || !h3FiltersApplied) {
      return h3Data;
    }

    const minSales = Number(h3SalesFilterRange[0]);
    const maxSalesVal = Number(h3SalesFilterRange[1]);
    const minTraffic = Number(h3TrafficFilterRange[0]);
    const maxTrafficVal = Number(h3TrafficFilterRange[1]);
    const minRevenue = Number(h3RevenueFilterRange[0]);
    const maxRevenueVal = Number(h3RevenueFilterRange[1]);

    return h3Data.filter(feature => {
      const props = feature.properties || feature;
      const salesValue = props.total_actual_sales || 0;
      const trafficValue = props.average_traffic_score || 0;
      const revenueValue = props.transaction_revenue || 0;

      const salesPass = salesValue >= minSales && salesValue <= maxSalesVal;
      const trafficPass = trafficValue >= minTraffic && trafficValue <= maxTrafficVal;
      const revenuePass = revenueValue >= minRevenue && revenueValue <= maxRevenueVal;

      return salesPass && trafficPass && revenuePass;
    });
  }, [h3Data, h3FiltersApplied, h3SalesFilterRange, h3TrafficFilterRange, h3RevenueFilterRange]);

  // --- LEAD DISPLAY LOGIC (moved before layers for dependency) ---
  // Filter by dynamic category (Level 1) using simpleFilters.entityType
  const myTerritoryActive = myTerritoryOnly && canUseMyTerritory(currentUser?.role, myTerritory.count);

  // The owned territories (with cells), for admins and HQ managers only:
  // reps are already scoped by the API.
  useEffect(() => {
    if (!currentUser || !['admin', 'hq_manager'].includes(currentUser.role)) return;
    axios.get(`${API_BASE_URL}/api/users/me/territories`, { params: { include_cells: true }, withCredentials: true })
      .then(res => {
        const territories = res.data?.territories || [];
        setMyTerritory({ count: territories.length, cells: cellsFromTerritories(territories) });
      })
      .catch(() => setMyTerritory({ count: 0, cells: new Set() }));
  }, [currentUser]);

  // The header narrows the briefing, route planner and graph to the territory.
  useEffect(() => { applyScopeHeader(myTerritoryActive); }, [myTerritoryActive]);

  const filteredLeadsByType = useMemo(() => {
    // Backend already filters by entity_type; the "My territory" view keeps
    // only leads in the caller's own cells.
    if (!myTerritoryActive) return allLeads;
    return filterToCells(allLeads, myTerritory.cells,
      lead => (lead.latitude && lead.longitude ? latLngToCell(lead.latitude, lead.longitude, H3_RESOLUTION) : null));
  }, [allLeads, myTerritoryActive, myTerritory.cells]);

  // Recalculate H3 aggregations based on currently filtered leads
  // This ensures H3 colors match the visible markers
  const h3DataWithRecalculatedAggregation = useMemo(() => {
    if (!filteredH3Data || filteredH3Data.length === 0) return filteredH3Data;

    // Group filtered leads by H3 cell (calculate from lat/long)
    const leadsByH3 = {};
    filteredLeadsByType.forEach(lead => {
      if (!lead.latitude || !lead.longitude) return;

      // Calculate H3 cell from coordinates
      const h3Cell = latLngToCell(lead.latitude, lead.longitude, H3_RESOLUTION);

      if (!leadsByH3[h3Cell]) {
        leadsByH3[h3Cell] = { customers: [], leads: [] };
      }

      // Separate customers (actual sales) from leads (forecasted sales)
      if (lead.category === 'current_customers') {
        leadsByH3[h3Cell].customers.push(lead);
      } else if (lead.category === 'leads') {
        leadsByH3[h3Cell].leads.push(lead);
      }
    });

    // Update each H3 feature with recalculated aggregations
    return filteredH3Data.map(feature => {
      const h3Index = feature.properties.h3_index;
      const cellLeads = leadsByH3[h3Index] || { customers: [], leads: [] };

      // Recalculate actual sales (from visible customers only)
      // MapEntity.value contains actual_sales for customers
      const total_actual_sales = cellLeads.customers.reduce(
        (sum, customer) => sum + (customer.value || 0),
        0
      );

      // Recalculate forecasted sales (from visible leads only)
      // MapEntity.value contains forecasted_sales for leads
      const total_forecasted_sales = cellLeads.leads.reduce(
        (sum, lead) => sum + (lead.value || 0),
        0
      );

      return {
        ...feature,
        properties: {
          ...feature.properties,
          total_actual_sales,
          total_forecasted_sales,
          current_customer_count: cellLeads.customers.length,
          potential_customer_count: cellLeads.leads.length
        }
      };
    });
  }, [filteredH3Data, filteredLeadsByType]);

  const h3GeoJson = useMemo(() => {
    if (!h3DataWithRecalculatedAggregation || h3DataWithRecalculatedAggregation.length === 0) return null;
    return { type: 'FeatureCollection', features: h3DataWithRecalculatedAggregation };
  }, [h3DataWithRecalculatedAggregation]);

  // Filter visits by selected intents
  const filteredVisitList = useMemo(() => {
    if (selectedIntents.includes('all')) return visitList;
    return visitList.filter(visit => selectedIntents.includes(visit.visit_reason));
  }, [visitList, selectedIntents]);

  const leadsToDisplay = useMemo(() => {
    if (!h3FiltersApplied || !isH3Visible) {
      return filteredLeadsByType;
    }

    return filteredLeadsByType.filter(lead => {
      if (lead.latitude && lead.longitude) {
        const leadH3Index = latLngToCell(lead.latitude, lead.longitude, H3_RESOLUTION);
        const isVisible = visibleH3Indexes.has(leadH3Index);
        return isVisible;
      }
      return false;
    });
  }, [filteredLeadsByType, visibleH3Indexes, h3FiltersApplied, isH3Visible]);

  // Zoom enters the layer build only as these two thresholds. Depending on the
  // raw viewState.zoom rebuilt all five layers on every frame of a zoom gesture
  // — the number changes continuously, the answers below change twice. Deriving
  // them here means the 360-line build below runs when a threshold is actually
  // crossed. isClusterZoom is also what the legend reads, so the comparison has
  // one definition rather than three.
  const isClusterZoom = viewState.zoom < CLUSTER_ZOOM;
  const showRevenueLabels = viewState.zoom >= REVENUE_LABEL_ZOOM;

  const layers = useMemo(() => {
    const layerArray = [];

    // Area dataset layer (org-imported data), below everything else
    if (datasetGeo && datasetGeo.features.length > 0) {
      layerArray.push(
        new GeoJsonLayer({
          id: 'area-dataset-layer',
          data: datasetGeo,
          opacity: 0.55,
          stroked: false,
          filled: true,
          pickable: true,
          getFillColor: d => rampColor(d.properties.rank),
          updateTriggers: { getFillColor: [datasetGeo] },
        })
      );
    }

    // H3 Hexagon Layer (rendered first, appears below markers)
    if (h3GeoJson && isH3Visible) {
      layerArray.push(
        new GeoJsonLayer({
          id: 'h3-deck-layer',
          data: h3GeoJson,
          visible: isH3Visible,
          opacity: 0.6,
          stroked: true,
          filled: true,
          pickable: true,

          getFillColor: d => {
            const props = d.properties || {};
            const totalSales = props.total_actual_sales || 0;
            const avgTraffic = props.average_traffic_score || 0;

            if (h3View === 'traffic') {
              // TRAFFIC VIEW: Blue gradient based on traffic score
              // If no traffic data, show very light gray instead of blue
              if (avgTraffic === 0) {
                return [220, 220, 220, 100];  // Very light gray, low opacity
              }
              return getColorByTrafficScore(avgTraffic);
            } else if (h3View === 'revenue') {
              // REVENUE VIEW: Amber gradient on TRANSACTION revenue
              // (geokg_area_org_metrics — anchored on the customer's cell,
              // rep-independent; distinct from the green CRM sales view)
              const txRevenue = props.transaction_revenue || 0;
              if (txRevenue === 0) {
                return [240, 240, 240, 80];
              }
              const revenueColors = [
                [255, 236, 179, 190],  // Very light amber - smallest revenue
                [255, 213, 128, 190],  // Light amber
                [255, 183, 77, 190],   // Medium amber
                [251, 140, 0, 190],    // Orange
                [230, 81, 0, 190]      // Deep orange - highest revenue
              ];
              const maxR = maxRevenue || 5000000;
              const normalizedRevenue = txRevenue / maxR;
              const revenue_bucket = Math.min(4, Math.floor(normalizedRevenue * 5));
              return revenueColors[revenue_bucket];
            } else if (h3View === 'population') {
              // POPULATION VIEW: Indigo gradient on population density (people/km²)
              const pop = props.population_density || 0;
              if (pop === 0) {
                return [240, 240, 240, 80];  // unseeded area (e.g. outside Tokyo)
              }
              const popColors = [
                [224, 231, 255, 190],  // Very light indigo - sparsest
                [165, 180, 252, 190],  // Light indigo
                [129, 140, 248, 190],  // Medium indigo
                [99, 102, 241, 190],   // Indigo
                [67, 56, 202, 190]     // Deep indigo - densest
              ];
              const maxP = maxPopulation || 20000;
              const pop_bucket = Math.min(4, Math.floor((pop / maxP) * 5));
              return popColors[pop_bucket];
            } else {
              // SALES VIEW: Green gradient based on ACTUAL sales (customers only)
              // If no actual sales (leads-only cells), show no color
              if (totalSales === 0) {
                return [240, 240, 240, 80];  // Very light gray, very low opacity (almost invisible)
              }

              const salesColors = [
                [200, 230, 201, 180],  // Very light green - smallest sales
                [165, 214, 167, 180],  // Light green
                [129, 199, 132, 180],  // Medium green
                [102, 187, 106, 180],  // Green
                [67, 160, 71, 180]     // Dark green - highest sales
              ];
              const maxS = maxSales || 5000000;
              const normalizedSales = totalSales / maxS;
              const sales_bucket = Math.min(4, Math.floor(normalizedSales * 5));
              return salesColors[sales_bucket];
            }
          },
          getLineColor: [0, 0, 0, 100],
          lineWidthMinPixels: 1,
          // Force re-render when view or data changes
          updateTriggers: {
            getFillColor: [h3View, maxSales, maxRevenue, maxPopulation],
            getData: [h3GeoJson]
          }
        })
      );
    }

    // Revenue view: label cells with their top product + revenue when zoomed
    // in enough to read them ("product level per region in the territory")
    if (h3GeoJson && isH3Visible && h3View === 'revenue' && showRevenueLabels) {
      const formatRevenueShort = (v) =>
        v >= 1000000 ? `¥${(v / 1000000).toFixed(1)}M`
          : v >= 1000 ? `¥${Math.round(v / 1000)}K`
          : `¥${Math.round(v)}`;
      const revenueLabels = (h3GeoJson.features || [])
        .filter(f => (f.properties?.transaction_revenue || 0) > 0)
        .map(f => {
          const ring = f.geometry?.coordinates?.[0] || [];
          const lng = ring.reduce((s, pt) => s + pt[0], 0) / (ring.length || 1);
          const lat = ring.reduce((s, pt) => s + pt[1], 0) / (ring.length || 1);
          const props = f.properties;
          const product = props.top_product ? `${props.top_product}\n` : '';
          return { lng, lat, text: `${product}${formatRevenueShort(props.transaction_revenue)}` };
        });
      layerArray.push(
        new TextLayer({
          id: 'h3-revenue-label-layer',
          data: revenueLabels,
          getPosition: d => [d.lng, d.lat],
          getText: d => d.text,
          getSize: 11,
          getColor: [66, 32, 6, 235],
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontWeight: 'bold',
          updateTriggers: { getText: [h3GeoJson] }
        })
      );
    }

    // Lead Markers Layer — zoom-adaptive rendering for scale:
    //   zoomed in  → individual IconLayer markers (GPU-accelerated, ~10K markers)
    //   zoomed out → one H3 hexagon per cell, labelled with its lead count
    if (leadsToDisplay && leadsToDisplay.length > 0) {

      if (isClusterZoom) {
        // Aggregate by H3 cell at resolution 6 — 33 km^2 in area, about 7.5 km
        // across — so the map shows one shape per area instead of thousands of
        // individual markers.
        //
        // Drawn as the cell's own hexagon rather than a circle. The count is a
        // per-cell number, so the cell is the honest footprint for it, and it
        // matches the resolution-9 hexagons this map already draws. It also
        // removes a positioning bug: as circles these were placed at the FIRST
        // lead's coordinates in the cell, not the cell's centre, so a bubble
        // could sit up to 3.7 km off — comparable to its own radius — and shift
        // as data loaded. A cell has exactly one position.
        //
        // Magnitude moves from radius to fill opacity, since every cell at a
        // given resolution is the same size.
        const clusterMap = {};
        for (const lead of leadsToDisplay) {
          if (lead.latitude == null || lead.longitude == null) continue;
          const cellKey = latLngToCell(lead.latitude, lead.longitude, 6);
          if (!clusterMap[cellKey]) clusterMap[cellKey] = 0;
          clusterMap[cellKey] += 1;
        }
        const clusters = Object.entries(clusterMap).map(([cell, count]) => {
          const [lat, lng] = cellToLatLng(cell);
          return { cell, count, lat, lng };
        });
        const maxCount = Math.max(...clusters.map(c => c.count), 1);

        layerArray.push(
          new GeoJsonLayer({
            id: 'lead-cluster-layer',
            data: {
              type: 'FeatureCollection',
              features: clusters.map(c => ({
                type: 'Feature',
                properties: { count: c.count },
                geometry: { type: 'Polygon', coordinates: [cellToBoundary(c.cell, true)] },
              })),
            },
            stroked: true,
            filled: true,
            pickable: false,
            // 90 to 200 alpha: the emptiest cell stays visible, the busiest is
            // clearly the busiest, and neither hides the map underneath.
            getFillColor: d => [59, 130, 246, 90 + (d.properties.count / maxCount) * 110],
            getLineColor: [255, 255, 255, 200],
            lineWidthMinPixels: 1,
            updateTriggers: { getFillColor: [leadsToDisplay, maxCount] }
          }),
          new TextLayer({
            id: 'lead-cluster-count-layer',
            data: clusters,
            getPosition: d => [d.lng, d.lat],
            getText: d => String(d.count),
            getSize: 13,
            getColor: [255, 255, 255, 240],
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'center',
            fontWeight: 'bold',
            outlineWidth: 2,
            outlineColor: [30, 58, 138, 200],
            fontSettings: { sdf: true },
            updateTriggers: { getText: [leadsToDisplay] }
          })
        );
      } else {
        // Helper to get marker color
        const getMarkerColor = (status, category) => {
          // Lead statuses — use status for fine-grained colors
          if (status === 'planned')   return '#7c3aed'; // purple
          if (status === 'contacted') return '#38bdf8'; // sky blue
          if (status === 'won')       return '#059669'; // green
          if (status === 'lost')      return '#9ca3af'; // gray
          if (status === 'new')       return '#3b82f6'; // blue
          // Customer statuses
          if (status === 'at_risk')   return '#f97316'; // orange
          if (status === 'churned')   return '#ef4444'; // red
          if (status === 'inactive')  return '#9ca3af'; // gray
          if (status === 'active')    return '#10b981'; // emerald
          // Category fallbacks
          switch (category) {
            case 'current_customers': return '#10b981';
            case 'at_risk_customers': return '#f97316';
            case 'won':               return '#059669';
            case 'inactive':          return '#9ca3af';
            default:                  return '#3b82f6';
          }
        };

        // Map briefing lead.id → its 1-based position, used to paint a numbered
        // badge on corresponding map markers so the user can pair the two sides
        // of the split at a glance.
        const briefingOrderById = new Map();
        memoizedDisplayedBriefing.forEach((bLead, idx) => {
          if (bLead && bLead.id != null) briefingOrderById.set(bLead.id, idx + 1);
        });

        // Build a marker SVG for a given lead based on its "state":
        // accepted (visit list) > focused > briefing-listed > normal.
        // Focused adds an extra purple outer ring on any base.
        const buildLeadIconSvg = (d) => {
          const isAccepted = activeVisitEntityIds.has(d.global_id);
          const briefingNum = briefingOrderById.get(d.id);
          const isBriefing = briefingNum !== undefined;
          const isFocused = focusedBriefingLeadId === d.id;

          // Canvas size scales with marker complexity so the focus ring fits.
          const size = isAccepted ? 36 : isBriefing ? 32 : isFocused ? 28 : 18;
          const c = size / 2;

          let body;
          if (isAccepted) {
            // Emerald w/ checkmark
            body = `
              <circle cx="${c}" cy="${c}" r="12" fill="#059669" stroke="white" stroke-width="2.5"/>
              <circle cx="${c}" cy="${c}" r="9" fill="none" stroke="rgba(255,255,255,0.4)" stroke-width="1.5"/>
              <polyline points="${c-5},${c} ${c-1},${c+4} ${c+6},${c-4}" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            `;
          } else if (isBriefing) {
            // Blue/purple gradient w/ number — matches brand, stands out from normal pins
            body = `
              <circle cx="${c}" cy="${c}" r="11" fill="#7c3aed" stroke="white" stroke-width="2.5"/>
              <text x="${c}" y="${c + 4}" text-anchor="middle" font-family="system-ui, -apple-system, sans-serif" font-size="12" font-weight="700" fill="white">${briefingNum}</text>
            `;
          } else {
            const color = getMarkerColor(d.status, d.category);
            body = `<circle cx="${c}" cy="${c}" r="7" fill="${color}" stroke="white" stroke-width="2"/>`;
          }

          // Focus ring: thick purple outline, rendered on top
          const ring = isFocused
            ? `<circle cx="${c}" cy="${c}" r="${c - 2}" fill="none" stroke="#a855f7" stroke-width="2.5" opacity="0.9"/>`
            : '';

          const svgStr = `<svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">${body}${ring}</svg>`;
          return {
            url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`,
            width: size,
            height: size,
            anchorY: c,
          };
        };

        const getSizeForLead = (d) => {
          if (activeVisitEntityIds.has(d.global_id)) return 36;
          if (briefingOrderById.has(d.id)) return 32;
          if (focusedBriefingLeadId === d.id) return 28;
          return 18;
        };

        layerArray.push(
          new IconLayer({
            id: 'lead-markers-layer',
            data: leadsToDisplay,
            getPosition: d => [d.longitude, d.latitude],
            getIcon: buildLeadIconSvg,
            getSize: getSizeForLead,
            sizeScale: 1,
            pickable: true,
            onClick: (info) => {
              if (info.object) {
                setShowDetailedView(false);
                setSelectedLead(info.object);
                // If this lead is in the current briefing, scroll the briefing
                // panel to its card by setting it as focused.
                if (briefingOrderById.has(info.object.id)) {
                  setFocusedBriefingLeadId(info.object.id);
                }
              }
            },
            updateTriggers: {
              getIcon: [leadsToDisplay, activeVisitEntityIds, memoizedDisplayedBriefing, focusedBriefingLeadId],
              getSize: [activeVisitEntityIds, memoizedDisplayedBriefing, focusedBriefingLeadId],
            }
          })
        );
      }
    }

    // Route polyline layer (rendered above markers when route is optimized)
    if (showRouteOnMap && optimizedRoute && optimizedRoute.legs && optimizedRoute.legs.length > 0) {
      if (routeGeoJson) {
        // Actual driving route from MapBox Directions API
        layerArray.push(
          new GeoJsonLayer({
            id: 'route-driving-layer',
            data: { type: 'Feature', geometry: routeGeoJson, properties: {} },
            stroked: true,
            filled: false,
            getLineColor: [59, 130, 246, 220],
            getLineWidth: 5,
            lineWidthMinPixels: 3,
            lineWidthMaxPixels: 8,
            lineCapRounded: true,
            lineJointRounded: true,
          })
        );
      } else {
        // Fallback: straight lines between stops
        const routePath = [];
        optimizedRoute.legs.forEach(leg => {
          routePath.push([leg.from_lng, leg.from_lat]);
        });
        const lastLeg = optimizedRoute.legs[optimizedRoute.legs.length - 1];
        routePath.push([lastLeg.to_lng, lastLeg.to_lat]);

        layerArray.push(
          new PathLayer({
            id: 'route-path-layer',
            data: [{ path: routePath }],
            getPath: d => d.path,
            getColor: [59, 130, 246, 200],
            getWidth: 4,
            widthMinPixels: 3,
            widthMaxPixels: 8,
            jointRounded: true,
            capRounded: true,
            billboard: false,
          })
        );
      }

      // Numbered stop markers
      const numberedStops = optimizedRoute.legs.map((leg, index) => ({
        position: [leg.to_lng, leg.to_lat],
        label: index + 1,
      }));

      layerArray.push(
        new IconLayer({
          id: 'route-numbered-markers',
          data: numberedStops,
          getPosition: d => d.position,
          getIcon: d => {
            const svgStr = `<svg width="28" height="28" xmlns="http://www.w3.org/2000/svg"><circle cx="14" cy="14" r="12" fill="#3b82f6" stroke="white" stroke-width="2"/><text x="14" y="18" text-anchor="middle" fill="white" font-size="13" font-weight="bold" font-family="Arial">${d.label}</text></svg>`;
            return {
              url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`,
              width: 28,
              height: 28,
              anchorY: 14,
            };
          },
          getSize: 28,
          sizeScale: 1,
          pickable: false,
          updateTriggers: {
            getIcon: [optimizedRoute],
          },
        })
      );
    }

    return layerArray;
  }, [datasetGeo, h3GeoJson, isH3Visible, h3View, maxSales, maxRevenue, maxPopulation, leadsToDisplay, showRouteOnMap, optimizedRoute, routeGeoJson, activeVisitEntityIds, memoizedDisplayedBriefing, focusedBriefingLeadId, isClusterZoom, showRevenueLabels]);
  // fetchDetailedEntity is stable (useCallback), showDetailedView is needed for onClick logic
  // --- END DECK.GL LAYER DEFINITION ---

  useEffect(() => {
    if (!h3Data) {
      return;
    }

    if (!h3FiltersApplied) {
      const allIndexes = new Set(h3Data.map(f => f.properties.h3_index));
      setVisibleH3Indexes(allIndexes);
      return;
    }

    const minSales = Number(h3SalesFilterRange[0]);
    const maxSalesVal = Number(h3SalesFilterRange[1]);
    const minTraffic = Number(h3TrafficFilterRange[0]);
    const maxTrafficVal = Number(h3TrafficFilterRange[1]);
    const minRevenue = Number(h3RevenueFilterRange[0]);
    const maxRevenueVal = Number(h3RevenueFilterRange[1]);

    const newVisibleIndexes = new Set();

    (h3Data || []).forEach(feature => {
      const props = feature.properties || feature;
      const salesValue = props.total_actual_sales || 0;
      const trafficValue = props.average_traffic_score || 0;
      const revenueValue = props.transaction_revenue || 0;

      const salesPass = salesValue >= minSales && salesValue <= maxSalesVal;
      const trafficPass = trafficValue >= minTraffic && trafficValue <= maxTrafficVal;
      const revenuePass = revenueValue >= minRevenue && revenueValue <= maxRevenueVal;

      if (salesPass && trafficPass && revenuePass) {
        newVisibleIndexes.add(props.h3_index);
      }
    });

    setVisibleH3Indexes(newVisibleIndexes);
    debug("Visible H3 indexes:", newVisibleIndexes.size);

  }, [h3SalesFilterRange, h3TrafficFilterRange, h3RevenueFilterRange, h3Data, h3FiltersApplied]);
  // --- END LEAD DISPLAY LOGIC ---

  // --- H3 HANDLERS ---
  const handleH3SalesRangeChange = (newRange) => {
    setIsH3Visible(true);
    setH3FiltersApplied(true); // Always mark as applied when slider changes
    setH3SalesFilterRange(newRange);
  };

  const handleH3TrafficRangeChange = (newRange) => {
    setIsH3Visible(true);
    setH3FiltersApplied(true); // Always mark as applied when slider changes
    setH3TrafficFilterRange(newRange);
  };

  const handleH3RevenueRangeChange = (newRange) => {
    setIsH3Visible(true);
    setH3FiltersApplied(true);
    setH3RevenueFilterRange(newRange);
  };

  const handleH3ViewChange = (view) => {
    if (isH3Visible && h3View === view) {
      // Unchecking the currently active layer - hide H3 but keep filters active
      setIsH3Visible(false);
      // Filters remain active even when layer is hidden
    } else {
      // Switching to a different layer or enabling a layer
      setIsH3Visible(true);
      setH3View(view);
      // Always ensure filters are applied when a layer is active
      setH3FiltersApplied(true);
    }
  };
  // --- END H3 HANDLERS ---


  // --- AGENT BRIEFING HANDLERS ---


  // Say why a briefing failed. It used to be "Is the server running?" for
  // every failure — including "your organisation has no GeoAgent access"
  // (402), which is what an org without an active subscription gets.
  const briefingErrorText = (error) => {
    if (error.code === 'ECONNABORTED') return 'The briefing took too long. Please try again in a minute.';
    if (!error.response) return 'Could not reach the server. Check your connection and try again.';
    const d = error.response.data?.detail;
    const msg = (d && d.message) || (typeof d === 'string' ? d : null);
    switch (error.response.status) {
      case 402: return msg || 'Your organisation does not have access to AI briefings. Ask your admin.';
      case 403: return msg || 'Your role does not have access to AI briefings.';
      default: return msg || `Could not get the briefing (server error ${error.response.status}).`;
    }
  };

  const handleGetBriefing = useCallback(() => {
    showNotification('Asking the agent for your daily briefing...');
    setIsBriefingLoading(true);
    if (!navigator.geolocation) {
      showNotification("Geolocation is not supported by your browser.", 5000);
      setIsBriefingLoading(false);
      return;
    }

    // Safari requires explicit options for reliable geolocation
    const geoOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 0 // Don't use cached position
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLon = position.coords.longitude;
        // Store location so the map dot is visible during the briefing
        setMyLocation({ latitude: userLat, longitude: userLon });
        axios.post(`${API_BASE_URL}/api/briefing`, { current_location: { lat: userLat, lng: userLon } }, { timeout: 90000 })
          .then(response => {
            const briefingData = response.data.data;
            const recs = briefingData?.briefing?.top_recommendations;
            if (Array.isArray(recs) && recs.length > 0) {
              setFullBriefingList(recs);
              setDisplayedBriefing(recs.slice(0, 5));
              setRejectedLeadIds(new Set()); // Clear rejected list for new briefing
              setIsMobileMenuOpen(false);
              setIsBriefingOpen(true);
              // Briefing takes the right panel — switch to map view so the left side
              // always shows the map, and close the visits panel so it doesn't compete.
              setCurrentView('map');
              setShowVisitList(false);
              setNotification('');
            } else if (Array.isArray(recs)) {
              // Valid briefing, but nothing to recommend (e.g. everyone in the
              // territory was recently contacted or is already on the route).
              // Do NOT open the panel — AgentBriefing renders null on an empty
              // list, which would otherwise leave a blank screen.
              setFullBriefingList([]);
              setDisplayedBriefing([]);
              setIsBriefingOpen(false);
              showNotification("No recommendations right now — all leads in your territory were recently contacted or are already on your route.", 6000);
            } else {
              showNotification("Could not parse the briefing data.", 5000);
            }
          })
          .catch(error => {
            console.error('Briefing API error:', error);
            showNotification(briefingErrorText(error), 8000);
          })
          .finally(() => setIsBriefingLoading(false));
      },
      (error) => {
        // If permission is denied, do not retry. It will just fail again.
        if (error.code === error.PERMISSION_DENIED) {
          showNotification("Location access denied. Please check Safari settings AND macOS System Settings > Privacy > Location Services.", 7000);
          setIsBriefingLoading(false);
          return;
        }

        console.warn('High accuracy geolocation failed, retrying with low accuracy...', error);

        // Fallback: Try again with low accuracy (Wi-Fi/IP)
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLat = position.coords.latitude;
            const userLon = position.coords.longitude;
            setMyLocation({ latitude: userLat, longitude: userLon });
            axios.post(`${API_BASE_URL}/api/briefing`, { current_location: { lat: userLat, lng: userLon } }, { timeout: 90000 })
              .then(response => {
                const briefingData = response.data.data;
                const recs = briefingData?.briefing?.top_recommendations;
                if (Array.isArray(recs) && recs.length > 0) {
                  setFullBriefingList(recs);
                  setDisplayedBriefing(recs.slice(0, 5));
                  setRejectedLeadIds(new Set()); // Clear rejected list for new briefing
                  setIsMobileMenuOpen(false);
                  setIsBriefingOpen(true);
                  setCurrentView('map');
                  setShowVisitList(false);
                  setNotification('');
                } else if (Array.isArray(recs)) {
                  // Valid briefing with no recommendations — show a message instead
                  // of opening an empty (blank) briefing panel.
                  setFullBriefingList([]);
                  setDisplayedBriefing([]);
                  setIsBriefingOpen(false);
                  showNotification("No recommendations right now — all leads in your territory were recently contacted or are already on your route.", 6000);
                } else {
                  showNotification("Could not parse the briefing data.", 5000);
                }
              })
              .catch(error => {
                console.error('Briefing API error:', error);
                showNotification(briefingErrorText(error), 8000);
              })
              .finally(() => setIsBriefingLoading(false));
          },
          (fallbackError) => {
            console.error('Geolocation fallback error:', fallbackError);
            let errorMessage = "Could not get your location to generate briefing.";

            switch (fallbackError.code) {
              case fallbackError.PERMISSION_DENIED:
                errorMessage = "Location permission denied. Please enable location access in your browser settings.";
                break;
              case fallbackError.POSITION_UNAVAILABLE:
                errorMessage = "Location information is unavailable.";
                break;
              case fallbackError.TIMEOUT:
                errorMessage = "Location request timed out. Please try again.";
                break;
              default:
                errorMessage = `Location error: ${fallbackError.message}`;
            }

            showNotification(errorMessage, 5000);
            setIsBriefingLoading(false);
          },
          {
            enableHighAccuracy: false, // Low accuracy fallback
            timeout: 20000, // give Wi-Fi/IP positioning more time before giving up
            maximumAge: 60000 // accept a cached fix up to 60s old to avoid needless timeouts
          }
        );
      },
      geoOptions
    );
  }, [showNotification]);

  const handleAcceptLead = useCallback(async (leadIdToAccept) => {
    const leadToAdd = fullBriefingList.find(lead => lead.id === leadIdToAccept);
    if (!leadToAdd) return;

    // Briefing single-accept: always schedule for today, reschedule if already planned
    const today = new Date();
    const scheduled_date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const globalId = leadToAdd.global_id || `lead_${leadToAdd.id}`;

    try {
      setActiveVisitEntityIds(prev => new Set([...prev, globalId]));
      await axios.post(`${API_BASE_URL}/api/visits`, {
        entity_global_id: globalId,
        scheduled_date,
        visit_reason: 'ai_briefing',
        reschedule_on_conflict: true,
      });
      await fetchVisitsWithFilter('today', 'route');
      setVisitFilterMode('today');
      setVisitViewMode('route');
      await fetchActiveVisitIds();
      showNotification(`${leadToAdd.name} added to today's route!`);
    } catch (error) {
      if (error.response?.status !== 409) {
        setActiveVisitEntityIds(prev => { const n = new Set(prev); n.delete(globalId); return n; });
        console.error('Failed to accept lead from briefing:', error);
        showNotification('Failed to add. Please try again.', 3000);
      }
      // 409 = checked_in, already active — silently OK
    }
  }, [fullBriefingList, showNotification, fetchVisitsWithFilter]);

  const handleAcceptAllLeads = useCallback(async () => {
    if (displayedBriefing.length === 0) return;

    setIsAcceptingAll(true);
    let addedCount = 0;
    let rescheduledCount = 0;

    try {
      const today = new Date();
      const scheduled_date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      // Only add the leads currently shown to the user (displayedBriefing = top 5).
      // fullBriefingList holds the full API result set; we never batch-add leads
      // the user hasn't seen.  reschedule_on_conflict moves overdue visits to today.
      for (const lead of displayedBriefing) {
        const globalId = lead.global_id || `lead_${lead.id}`;
        try {
          // Optimistic update
          setActiveVisitEntityIds(prev => new Set([...prev, globalId]));

          const response = await axios.post(`${API_BASE_URL}/api/visits`, {
            entity_global_id: globalId,
            scheduled_date: scheduled_date,
            visit_reason: 'ai_briefing',
            reschedule_on_conflict: true,   // reschedule old planned visits to today
          });
          // 200 = new visit created; backend also returns 200 for reschedule
          if (response.data?.scheduled_date === scheduled_date) {
            // Could be new or rescheduled — count as success either way
            addedCount++;
          } else {
            addedCount++;
          }
        } catch (error) {
          if (error.response?.status === 409) {
            // 409 means checked_in (can't reschedule mid-visit) — that's fine, it's already active
            rescheduledCount++; // Still "in today's route" in spirit
            debug(`${lead.name || globalId} is checked_in — already active`);
          } else {
            // Revert optimistic update for this one
            setActiveVisitEntityIds(prev => {
              const next = new Set(prev);
              next.delete(globalId);
              return next;
            });
            console.error(`Failed to add ${lead.name || globalId}:`, error);
          }
        }
      }

      // Single bulk refetch after all POSTs complete — always switch to Today
      const targetFilter = 'today';
      const targetView = 'route';
      if (targetFilter !== visitFilterMode) setVisitFilterMode(targetFilter);
      if (targetView !== visitViewMode) setVisitViewMode(targetView);

      await fetchVisitsWithFilter(targetFilter, targetView);
      await fetchTotalVisitCount();
      await fetchActiveVisitIds();

      // Clear optimized route (new visits invalidate it)
      setOptimizedRoute(null);
      setRouteGeoJson(null);
      setShowRouteOnMap(false);

      const total = addedCount + rescheduledCount;
      if (total > 0) {
        showNotification(`${total} recommendation${total !== 1 ? 's' : ''} added to today's route!`);
      } else {
        showNotification('Nothing to add.', 3000);
      }
    } finally {
      setIsAcceptingAll(false);
    }
  }, [displayedBriefing, visitFilterMode, visitViewMode, showNotification, fetchVisitsWithFilter, fetchTotalVisitCount]);

  const handleSaveRoute = async () => {
    // Save Route - Persist today's visit plan
    // NOTE: Visits are already saved to the visits table when added via "Add to Visit List"
    // This function provides confirmation that the route is ready
    // ARCHITECTURAL FIX: We NO LONGER set CRM status to 'planned_visit'
    // Visit planning is tracked in the visits table, NOT in CRM statuses

    if (visitList.length === 0) {
      showNotification("No visits in your route.", 3000);
      return;
    }

    try {
      // Refresh visit list using current filter mode and view mode
      const allActiveVisits = await fetchVisitsWithFilter(visitFilterMode, visitViewMode);

      // Update total visit count for QuickMenu visibility
      await fetchTotalVisitCount();

      showNotification(`✅ Route saved! ${allActiveVisits.length} visits ready.`, 3000);

      debug(`Route saved: ${allActiveVisits.length} visits (filter: ${visitFilterMode})`);

    } catch (error) {
      console.error("Failed to save route:", error);
      showNotification("Failed to refresh route. Please try again.", 3000);
    }
  };

  const handleRejectLead = useCallback((leadToReject) => {
    // Find the full lead object from displayedBriefing
    const lead = displayedBriefing.find(l => l.id === leadToReject);
    if (lead) {
      setEntityToReject(lead);
      setShowRejectionModal(true);
    }
  }, [displayedBriefing]);

  const handleConfirmRejection = async (entity, rejectionType, rejectionReason) => {
    setLoadingLeadId(entity.id);

    try {
      // Save rejection to database
      await axios.post(`${API_BASE_URL}/api/rejections`, {
        entity_global_id: entity.global_id,
        rejection_type: rejectionType,
        rejection_reason: rejectionReason || null
      });

      // Track rejected lead ID to prevent it from reappearing in this session
      setRejectedLeadIds(prev => new Set([...prev, entity.id]));

      // Find next lead that is:
      // 1. NOT currently displayed
      // 2. NOT already rejected
      const nextLead = fullBriefingList.find(
        lead => !displayedBriefing.some(d => d.id === lead.id) &&
          !rejectedLeadIds.has(lead.id) &&
          lead.id !== entity.id
      );

      if (nextLead) {
        // Replace rejected lead with next lead
        setDisplayedBriefing(prev =>
          prev.map(lead => lead.id === entity.id ? nextLead : lead)
        );
      } else {
        // No more leads available, just remove the rejected one
        setDisplayedBriefing(prev => prev.filter(lead => lead.id !== entity.id));
        showNotification("No more recommendations available.", 3000);
      }

      showNotification(`Saved: Won't show ${entity.name} for ${rejectionType === 'hard_no' ? '1 year' : rejectionType === 'not_now' ? '1 week' : '1 day'}`, 3000);
    } catch (error) {
      console.error('Failed to save rejection:', error);
      showNotification('Failed to save rejection. Please try again.', 3000);
    } finally {
      setLoadingLeadId(null);
      setShowRejectionModal(false);
      setEntityToReject(null);
    }
  };

  const handleCloseBriefing = () => {
    setIsBriefingOpen(false);
    setFullBriefingList([]);
    setRejectedLeadIds(new Set()); // Clear rejected list when closing
    setDisplayedBriefing([]);
    setFocusedBriefingLeadId(null);
  };

  // Fly the map to a briefing lead and mark it as focused.
  // Called when user clicks a briefing card OR clicks a matching map marker.
  // Reads the map's own zoom rather than depending on viewState.zoom. With that
  // dependency this callback was rebuilt on every frame of a zoom gesture, and
  // AgentBriefing's memo comparator checks onLeadFocus by identity — so an open
  // briefing re-rendered every frame, running JSON.stringify over the whole
  // briefing payload each time. The ref is also the more accurate source: it is
  // the live value, not one state update behind.
  const handleFocusBriefingLead = useCallback((lead) => {
    if (!lead) return;
    setFocusedBriefingLeadId(lead.id);
    // react-map-gl v7: ref exposes flyTo directly
    const map = mapRef.current;
    if (map && typeof map.flyTo === 'function' && lead.longitude && lead.latitude) {
      const currentZoom = typeof map.getZoom === 'function' ? map.getZoom() : 16;
      map.flyTo({
        center: [lead.longitude, lead.latitude],
        zoom: Math.max(currentZoom, 16),
        duration: 800,
        essential: true,
      });
    }
  }, []);
  // --- END AGENT BRIEFING HANDLERS ---

  // --- COMPONENT RENDERING ---

  // Google OAuth callback route — render standalone (no auth gate needed,
  // user arrives here via redirect from Google after consent screen)
  if (window.location.pathname === '/auth/google/callback') {
    return <GoogleOAuthCallback />;
  }

  // While session check is in flight, show a neutral loading screen
  if (authLoading) {
    return (
      <div style={{ height: 'var(--app-height)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(var(--c-gray-950))' }}>
        <div style={{ color: 'rgb(var(--c-gray-500))', fontSize: '14px' }}>Loading…</div>
      </div>
    );
  }

  // During logout + redirect, show a neutral screen instead of flashing Login.js
  if (loggingOut) {
    return (
      <div style={{ height: 'var(--app-height)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgb(var(--c-gray-950))' }}>
        <div style={{ color: 'rgb(var(--c-gray-500))', fontSize: '14px' }}>Logging out…</div>
      </div>
    );
  }

  // When not authenticated and no external marketing-site is configured (local dev),
  // show the built-in login page so the user stays on this origin after login.
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Platform Home — full-screen product launcher, no sidebar
  if (currentView === 'platform') {
    return (
      <PlatformHome
        currentUser={currentUser}
        onEnterGeoAgent={() => setCurrentView('map')}
        onEnterConcierge={async () => {
          const conciergeUrl = process.env.REACT_APP_CONCIERGE_URL || 'http://localhost:3002';
          window.location.href = await handoffUrl(conciergeUrl);
        }}
        onLogout={handleLogout}
      />
    );
  }

  // The map is on screen for more than the 'map' view: the visits panel and the
  // AI briefing both keep it beside them. Its controls were gated on
  // currentView === 'map' alone, so opening either made the legend and the
  // my-location button vanish while the map itself stayed — and with no button
  // there was no way to drop the current-location pin. Controls follow the map.
  const isMapVisible =
    currentView === 'map' || currentView === 'visits' || isBriefingOpen || showVisitList;

  return (
    <div className="h-[var(--app-height)] bg-gray-900 flex overflow-hidden">{/* Version 19 horizontal layout */}
      {/* MobileNav Sheet — opened by BottomNav Menu tab, replaces hamburger on mobile */}
      {!isFocusMode && (
        <MobileNav
          isOpen={showMobileNav}
          onClose={() => setShowMobileNav(false)}
          currentView={currentView}
          onViewChange={handleViewChange}
          onGetBriefing={handleGetBriefing}
          currentUser={currentUser}
          visitCount={totalVisitCount}
          workCount={taskCount + handoverCount}
          isBriefingActive={isBriefingOpen}
          onSimpleFilterChange={setSimpleFilters}
          currentSimpleFilters={simpleFilters}
          isH3Visible={isH3Visible}
          h3ActiveView={h3View}
          areaLayers={areaLayers}
          areaDatasets={areaDatasets}
          activeDatasetLayer={datasetLayer}
          onDatasetLayerChange={handleDatasetLayerChange}
          onH3ViewChange={handleH3ViewChange}
          h3SalesFilterRange={h3SalesFilterRange}
          h3TrafficFilterRange={h3TrafficFilterRange}
          h3RevenueFilterRange={h3RevenueFilterRange}
          maxSalesValue={maxSales}
          maxTrafficValue={maxTraffic}
          maxRevenueValue={maxRevenue}
          onH3SalesRangeChange={handleH3SalesRangeChange}
          onH3TrafficRangeChange={handleH3TrafficRangeChange}
          onH3RevenueRangeChange={handleH3RevenueRangeChange}
          onResetH3Filters={handleResetH3Filters}
          onShowProfile={() => setCurrentView('profile')}
          onShowSettings={() => setShowSettings(true)}
          onShowMySettings={() => handleViewChange('my-settings')}
          onShowTeamSettings={() => handleViewChange('team-settings')}
        />
      )}

      {/* NavRail — desktop-only collapsible left rail */}
      {!isFocusMode && (
        <NavRail
          activeView={currentView}
          onViewChange={handleViewChange}
          currentUser={currentUser}
          onLogout={handleLogout}
          onOpenSettings={() => handleViewChange('my-settings')}
          onOpenTeamSettings={() => handleViewChange('team-settings')}
          onOpenProfile={() => setCurrentView('profile')}
          onGetBriefing={handleGetBriefing}
          visitCount={totalVisitCount}
          workCount={taskCount + handoverCount}
          isBriefingLoading={isBriefingLoading}
          isBriefingOpen={isBriefingOpen}
        />
      )}

      {/* SideBar — always shown (except focus mode / platform home). Filters must remain
          accessible even while the AI Briefing split is open. */}
      {/* The rail is a filter panel, so it only appears for views that have
          filters. Admin Hub and Profile used to show the map's entity filters,
          layer toggles and H3 sliders because SideBar's switch fell through to
          the map case. */}
      {!isFocusMode && hasFilterPanel(currentView) && (
        <SideBar
          onFilterChange={handleCustomerTypeChange}
          activeFilter={customerTypeFilter}
          customerStatuses={customerStatuses}
          categories={categories}
          onViewChange={handleViewChange}
          activeView={currentView}
          onH3SalesRangeChange={handleH3SalesRangeChange}
          h3SalesFilterRange={h3SalesFilterRange}
          onH3TrafficRangeChange={handleH3TrafficRangeChange}
          h3TrafficFilterRange={h3TrafficFilterRange}
          onH3RevenueRangeChange={handleH3RevenueRangeChange}
          h3RevenueFilterRange={h3RevenueFilterRange}
          maxSalesValue={maxSales}
          maxTrafficValue={maxTraffic}
          maxRevenueValue={maxRevenue}
          onH3ViewChange={handleH3ViewChange}
          h3ActiveView={h3View}
          areaLayers={areaLayers}
          areaDatasets={areaDatasets}
          activeDatasetLayer={datasetLayer}
          onDatasetLayerChange={handleDatasetLayerChange}
          isH3Visible={isH3Visible}
          onResetH3Filters={handleResetH3Filters}
          onLogout={handleLogout}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
          onShowProfile={() => setCurrentView('profile')}
          onShowSettings={() => setShowSettings(true)}
          onShowMySettings={() => handleViewChange('my-settings')}
          onShowTeamSettings={() => handleViewChange('team-settings')}
          currentUser={currentUser}
          onSimpleFilterChange={setSimpleFilters}
          currentSimpleFilters={simpleFilters}
          visitViewMode={visitViewMode}
          onVisitViewModeChange={handleVisitViewModeChange}
          visitFilterMode={visitFilterMode}
          onVisitFilterChange={handleVisitFilterChange}
          overdueCount={overdueCount}
          onCollapsedChange={setSidebarCollapsed}
        />
      )}
      <div className={`flex-1 flex flex-col overflow-hidden ${isFocusMode ? 'w-full' : ''}`}>{/* Main content column */}
        {/* The KPI strip above the map is removed for now. StatsBar itself is
            left in place so it can be restored, but note before doing so: its
            figures fall back to invented ones when there is no lead data
            (revenue 825370, accounts 40153) and its captions — "Trending up
            this month", "Down 62% this period" — are hardcoded, not computed. */}

        {/* ── Horizontal split: Map (left, flex-1) + Right panel (fixed width) ── */}
        <div className="flex-1 flex overflow-hidden">

        <MainContent>

          {/* Map with Deck.gl overlay */}
          <div
            ref={mapContainerRef}
            className="map-container"
            style={{ display: isMapVisible ? 'block' : 'none', position: 'relative' }}
          >
            <MapGL
              ref={mapRef}
              {...viewState}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/streets-v12"
              mapboxAccessToken={MAPBOX_TOKEN}
              fog={null}
              onLoad={() => {
                // map style and internal state are ready
                setMapReady(true);
              }}
              onMove={(evt) => {
                const vs = evt.viewState;
                setViewState(prev => ({ ...prev, ...vs }));

                // Viewport-aware loading: debounce re-fetch after pan/zoom stops.
                // Only when the full set was too big to load (entitiesTruncatedRef),
                // at zoom >= 11 (city-level) to keep request sizes small.
                // Skips ai_picks — those are already bounded by the briefing recommendation list.
                if (entitiesTruncatedRef.current && simpleFilters.primary !== 'ai_picks' && vs.zoom >= 11) {
                  clearTimeout(viewportFetchTimer.current);
                  viewportFetchTimer.current = setTimeout(() => {
                    if (!mapRef.current) return;
                    try {
                      const bounds = mapRef.current.getBounds();
                      if (!bounds) return;
                      const bbox = {
                        bbox_sw_lat: bounds.getSouth().toFixed(6),
                        bbox_sw_lng: bounds.getWest().toFixed(6),
                        bbox_ne_lat: bounds.getNorth().toFixed(6),
                        bbox_ne_lng: bounds.getEast().toFixed(6),
                      };
                      // Only re-fetch if viewport moved meaningfully (>0.01°)
                      const prev = lastFetchedBounds.current;
                      const moved = !prev ||
                        Math.abs(bbox.bbox_sw_lat - prev.bbox_sw_lat) > 0.01 ||
                        Math.abs(bbox.bbox_sw_lng - prev.bbox_sw_lng) > 0.01;
                      if (moved) {
                        lastFetchedBounds.current = bbox;
                        fetchMapEntitiesRef.current(bbox);
                      }
                    } catch (e) { /* map not ready */ }
                  }, 600); // 600ms debounce
                }
              }}
              onClick={(evt) => {
                // Handle clicks on map to detect lead marker clicks
                // Since DeckGL has pointerEvents: 'none', we need to manually pick
                const { lngLat } = evt;
                if (!lngLat || !leadsToDisplay) return;

                // Find closest lead within 20 pixels
                const clickThreshold = 20 / Math.pow(2, viewState.zoom); // Adjust threshold by zoom level
                let closestLead = null;
                let minDistance = Infinity;

                leadsToDisplay.forEach(lead => {
                  const dx = lead.longitude - lngLat.lng;
                  const dy = lead.latitude - lngLat.lat;
                  const distance = Math.sqrt(dx * dx + dy * dy);

                  if (distance < clickThreshold && distance < minDistance) {
                    minDistance = distance;
                    closestLead = lead;
                  }
                });

                // If detail panel is open and user clicks the map
                if (showDetailedView) {
                  setShowDetailedView(false); // Always close the panel

                  if (closestLead) {
                    // Clicked on a marker → close panel, show mini popup for new marker
                    setSelectedLead(closestLead);
                  } else {
                    // Clicked on empty space → close panel, clear selection
                    setSelectedLead(null);
                  }
                } else {
                  // Panel not open, normal behavior
                  if (closestLead) {
                    setSelectedLead(closestLead);
                  }
                }
              }}
            >
              {mapReady && (
                <DeckGL
                  viewState={viewState}
                  controller={false} // MapBox handles pan/zoom/rotate
                  layers={layers}
                  style={{ pointerEvents: 'none' }} // Exact same as main branch - smooth zoom
                />
              )}

              {/* My Location Marker - kept as DOM for simplicity (only 1 marker) */}
              {myLocation && (
                <Marker
                  longitude={myLocation.longitude}
                  latitude={myLocation.latitude}
                >
                  <div className="my-location-marker" />
                </Marker>
              )}

            </MapGL>

            {/* Draggable mini popup card */}
            {selectedLead && !showDetailedView && (
              <DraggableMapCard
                selectedLead={selectedLead}
                mapRef={mapRef}
                onClose={() => setSelectedLead(null)}
                onViewDetails={() => {
                  if (selectedLead.global_id) {
                    fetchDetailedEntity(selectedLead.global_id, true);
                  } else {
                    setIsMobileMenuOpen(false);
                    setShowDetailedView(true);
                  }
                }}
                isLoadingDetails={isLoadingDetails}
              />
            )}

            {/* Map Legend — absolute inside map container so it stays within the map column */}
            {isMapVisible && (
              <MapLegend
                isH3Visible={isH3Visible}
                h3ActiveView={h3View}
                datasetLegend={datasetMetric && datasetGeo ? { title: metricLabel(datasetMetric), min: datasetMetric.min_value, max: datasetMetric.max_value } : null}
                categories={categories}
                isClustered={isClusterZoom}
              />
            )}

            {/* "My territory" toggle — admins / HQ managers who own a territory */}
            {isMapVisible && canUseMyTerritory(currentUser?.role, myTerritory.count) && (
              <button
                onClick={() => { const next = !myTerritoryOnly; setMyTerritoryOnly(next); storeToggle(next); }}
                aria-pressed={myTerritoryOnly}
                title={myTerritoryOnly ? 'Showing your territory. Click to show the whole organization.' : 'Showing the whole organization. Click to show only your territory.'}
                className={`absolute top-3 left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg border transition-colors ${myTerritoryOnly ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700'}`}
              >
                {myTerritoryOnly ? 'My territory' : 'Whole organization'}
              </button>
            )}

            {/* My Location button — bottom-right of map area */}
            {isMapVisible && (
              <button aria-label="Show my location"
                onClick={handleShowMyLocation}
                title="Show my location"
                className="absolute map-overlay-bottom-right z-30 bg-gray-800 rounded-full w-11 h-11 flex items-center justify-center shadow-lg border border-gray-700 hover:bg-gray-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                  <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" fill="currentColor" fillOpacity="0.15"/>
                </svg>
              </button>
            )}
          </div>

          {notification && <div className="notification">{notification}</div>}
          {/* Cover (rule R3): say why another territory's stores are on the map */}
          {currentView === 'map' && coveringNow.length > 0 && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 max-w-[90%] rounded-lg bg-emerald-900/90 text-emerald-100 text-xs px-3 py-1.5 shadow">
              {coveringNow.map(c => `Covering ${c.territory.name}${c.covered ? ` for ${c.covered.name}` : ''} until ${new Date(`${c.ends_on}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`).join(' · ')}
            </div>
          )}
          {currentView === 'list' && <ListView leads={leadsToDisplay} activeFilter={simpleFilters.primary} unplaced={unplacedCount} />}

          {/* Dashboard View */}
          {currentView === 'dashboard' && (
            <Deferred>
              <DashboardView
                currentUser={currentUser}
                onViewChange={handleViewChange}
              />
            </Deferred>
          )}

          {/* Graph Intelligence — NL query panel */}
          {currentView === 'graph' && (
            <Deferred>
              <GraphQueryPanel currentUser={currentUser} />
            </Deferred>
          )}

          {/* My Settings and Team Settings are pages, like the Admin Hub's tabs —
              no settings screen opens as a pop-up. */}
          {currentView === 'my-settings' && (
            <Deferred>
              <div className="max-w-3xl mx-auto p-4 md:p-6">
                <MySettings currentUser={currentUser} embedded />
              </div>
            </Deferred>
          )}
          {currentView === 'team-settings' && (
            <Deferred>
              <div className="max-w-5xl mx-auto p-4 md:p-6">
                <TeamSettingsDashboard currentUser={currentUser} embedded />
              </div>
            </Deferred>
          )}

          {/* Customers — accounts and their stores across territories (KAM home, D2) */}
          {currentView === 'accounts' && (
            <Deferred>
              <AccountsPanel />
            </Deferred>
          )}

          {/* Tasks — work people give each other; Handovers is one of its tabs */}
          {currentView === 'tasks' && (
            <Deferred>
              <TasksPanel
                currentUser={currentUser}
                initialTab={tasksTab}
                onChanged={fetchHandoverCount}
              />
            </Deferred>
          )}

          {/* Admin Hub — setup & operations home (admin role only) */}
          {currentView === 'admin' && currentUser?.role === 'admin' && (
            <Deferred>
              <AdminHub
                currentUser={currentUser}
                onImportComplete={() => { fetchMapEntities(); fetchAreaDatasets(); }}
                mapCenter={{ latitude: viewState.latitude, longitude: viewState.longitude }}
                onLogout={handleLogout}
              />
            </Deferred>
          )}
        </MainContent>

        {/* ── Right panel: Detail / Briefing / Visits alongside the map (desktop only) ──
            Width is user-resizable via ResizeHandle.
            Mobile: full-screen overlays below handle these views.             */}
        {(showDetailedView && selectedLead && !isFloating) || isBriefingOpen || (!isBriefingOpen && currentView === 'visits') ? (
          <ResizeHandle rightPanelWidth={rightPanelWidth} onResize={handleRightPanelResize} />
        ) : null}
        {showDetailedView && selectedLead && !isFloating && (
          <div
            className="hidden md:flex flex-col min-w-0 border-l border-gray-700 bg-gray-800 overflow-hidden"
            style={{ width: rightPanelWidth }}
          >
            <LeadDetailPanel
              lead={selectedLead}
              panelMode={true}
              isFloating={false}
              onToggleFloat={() => setIsFloating(true)}
              onClose={() => {
                setShowDetailedView(false);
                setSelectedLead(null);
                if (visitList.length > 0) {
                  setIsMobileMenuOpen(false);
                  setShowVisitList(true);
                }
              }}
              onAddToVisitList={handleAddToVisitList}
              isInVisitList={isLeadInVisitList}
            />
          </div>
        )}
        {isBriefingOpen && (
          <div
            className="hidden md:flex flex-col min-w-0 border-l border-gray-700 bg-gray-900 overflow-hidden"
            style={{ width: rightPanelWidth }}
          >
              <AgentBriefing
                briefingData={memoizedDisplayedBriefing}
                onClose={handleCloseBriefing}
                onAccept={handleAcceptLead}
                onAcceptAll={handleAcceptAllLeads}
                onReject={handleRejectLead}
                isLeadInVisitList={isLeadInVisitList}
                loadingLeadId={loadingLeadId}
                isAcceptingAll={isAcceptingAll}
                onLeadFocus={handleFocusBriefingLead}
                focusedLeadId={focusedBriefingLeadId}
                panelMode={true}
              />
          </div>
        )}
        {!isBriefingOpen && currentView === 'visits' && (
          <div
            className="hidden md:flex flex-col min-w-0 border-l border-gray-700 bg-gray-900 overflow-hidden"
            style={{ width: rightPanelWidth }}
          >
            <TodaysVisits
              visitList={filteredVisitList}
              onRemove={handleRemoveFromVisitList}
              onClear={handleClearVisitList}
              onSync={handleSaveRoute}
              onClose={() => { setShowVisitList(false); setCurrentView('map'); }}
              onItemClick={(item) => { fetchDetailedEntity(item.entity_global_id, true); }}
              onCheckIn={handleCheckIn}
              onCompleteVisit={handleCompleteVisit}
              onFilterChange={handleVisitFilterChange}
              geofenceActive={geofenceActive}
              viewMode={visitViewMode}
              onViewModeChange={handleVisitViewModeChange}
              undoableActions={undoableActions}
              onUndo={handleUndo}
              onOptimizeRoute={handleOptimizeRoute}
              isOptimizing={isOptimizing}
              optimizedRoute={optimizedRoute}
              showRouteOnMap={showRouteOnMap}
              onToggleRouteOnMap={() => setShowRouteOnMap(prev => !prev)}
              onReschedule={handleRescheduleVisit}
              visitPermissions={visitPermissions}
              overdueCount={overdueCount}
              overdueList={overdueList}
              visitsLoading={visitsLoading}
              parentFilterMode={visitFilterMode}
              onLeadStatusChange={(globalId, newStatus) => {
                setAllLeads(prev => prev.map(e =>
                  e.global_id === globalId ? { ...e, status: newStatus } : e
                ));
              }}
              panelMode={true}
              hideInternalFilters={true}
            />
          </div>
        )}

        </div>{/* end horizontal split */}

      </div>

      {/* Mobile full-screen overlays — briefing and visits take the whole screen */}
      {isBriefingOpen && (
        <div className="md:hidden">
          <AgentBriefing
            briefingData={memoizedDisplayedBriefing}
            onClose={handleCloseBriefing}
            onAccept={handleAcceptLead}
            onAcceptAll={handleAcceptAllLeads}
            onReject={handleRejectLead}
            isLeadInVisitList={isLeadInVisitList}
            loadingLeadId={loadingLeadId}
            isAcceptingAll={isAcceptingAll}
            onLeadFocus={handleFocusBriefingLead}
            focusedLeadId={focusedBriefingLeadId}
            mobileSheet={true}
          />
        </div>
      )}
      {!isBriefingOpen && (showVisitList || currentView === 'visits') && (
        <div
          className="md:hidden fixed inset-0 z-40 flex flex-col bg-gray-900"
          style={{ paddingBottom: 'var(--bottom-nav-height)' }}
        >
          <TodaysVisits
            visitList={filteredVisitList}
            onRemove={handleRemoveFromVisitList}
            onClear={handleClearVisitList}
            onSync={handleSaveRoute}
            onClose={() => { setShowVisitList(false); if (currentView === 'visits') setCurrentView('map'); }}
            onItemClick={(item) => { fetchDetailedEntity(item.entity_global_id, true); setShowVisitList(false); }}
            onCheckIn={handleCheckIn}
            onCompleteVisit={handleCompleteVisit}
            onFilterChange={handleVisitFilterChange}
            geofenceActive={geofenceActive}
            viewMode={visitViewMode}
            onViewModeChange={handleVisitViewModeChange}
            undoableActions={undoableActions}
            onUndo={handleUndo}
            onOptimizeRoute={handleOptimizeRoute}
            isOptimizing={isOptimizing}
            optimizedRoute={optimizedRoute}
            showRouteOnMap={showRouteOnMap}
            onToggleRouteOnMap={() => setShowRouteOnMap(prev => !prev)}
            onReschedule={handleRescheduleVisit}
            visitPermissions={visitPermissions}
            overdueCount={overdueCount}
            overdueList={overdueList}
            visitsLoading={visitsLoading}
            parentFilterMode={visitFilterMode}
            onLeadStatusChange={(globalId, newStatus) => {
              setAllLeads(prev => prev.map(e =>
                e.global_id === globalId ? { ...e, status: newStatus } : e
              ));
            }}
            panelMode={true}
          />
        </div>
      )}

      {/* AI Briefing FAB removed — desktop uses NavRail, mobile uses BottomNav AI pill */}


      {/* Intent Filter - Show when visit list is visible */}
      {currentView === 'map' && showVisitList && visitList.length > 0 && !showDetailedView && !isBriefingOpen && (
        <div className="fixed z-30 left-4 md:left-6 top-[calc(5rem_+_var(--safe-top))] md:top-44">
          <IntentFilter
            selectedIntents={selectedIntents}
            onIntentChange={handleIntentChange}
          />
        </div>
      )}

      {/* Visits panel now lives in the left panel slot above — removed floating overlay */}

      {/* Detailed Lead Panel — mobile bottom sheet OR floating overlay */}
      {showDetailedView && selectedLead && (isFloating || true) && (
        <div className={isFloating ? 'block' : 'md:hidden'}>
          <LeadDetailPanel
            lead={selectedLead}
            panelMode={false}
            isFloating={isFloating}
            onToggleFloat={() => setIsFloating(prev => !prev)}
            onClose={() => {
              setShowDetailedView(false);
              setSelectedLead(null);
              setIsFloating(false);
              if (visitList.length > 0) {
                setIsMobileMenuOpen(false);
                setShowVisitList(true);
              }
            }}
            onAddToVisitList={handleAddToVisitList}
            isInVisitList={isLeadInVisitList}
          />
        </div>
      )}

      {/* Today's Insights - Show on any page when toggled from Quick Menu */}
      {showInsights && !showDetailedView && !isBriefingOpen && !showVisitList && (
        <TodaysInsights onClose={() => setShowInsights(false)} />
      )}

      {/* Profile View */}
      {currentView === 'profile' && (
        <Deferred>
          <ProfileView
            onLogout={handleLogout}
            currentUser={currentUser}
          />
        </Deferred>
      )}

      {/* Organization Settings - Admin Only */}
      {showSettings && (
        <Deferred overlay>
          <OrgSettings
            currentUser={currentUser}
            onClose={() => setShowSettings(false)}
          />
        </Deferred>
      )}

      {/* §23: Admin Org Services (subscription toggles) */}
      {showOrgServices && (
        <Deferred overlay>
          <OrgServices
            currentUser={currentUser}
            onClose={() => {
              setShowOrgServices(false);
              // Clearing the lock banner if an admin just re-enabled the service
              setLockedService(null);
            }}
          />
        </Deferred>
      )}

      {/* Trial expiry banner — amber bar shown to admins when ≤7 days remain.
          Session-dismissed; does not block the UI. */}
      {billingState?.status === 'trial' &&
       billingState.daysLeft !== null &&
       billingState.daysLeft <= 7 &&
       !trialBannerDismissed &&
       currentUser?.role === 'admin' && (
        /* Pinned to the very top, so on a home-screen iPhone it would render under
           the status bar. Pad by the safe-area insets rather than moving the bar. */
        <div
          className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 text-sm font-medium px-4 py-2.5 flex items-center justify-between shadow-md"
          style={{
            paddingTop: 'calc(0.625rem + var(--safe-top))',
            paddingLeft: 'calc(1rem + var(--safe-left))',
            paddingRight: 'calc(1rem + var(--safe-right))',
          }}
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {billingState.daysLeft > 0
              ? <span>Your free trial ends in <strong>{billingState.daysLeft} day{billingState.daysLeft === 1 ? '' : 's'}</strong>. Choose a plan to keep your team on NorthAI.</span>
              : <span>Your free trial ends today. Choose a plan to keep your team on NorthAI.</span>
            }
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <button
              onClick={() => setShowOrgServices(true)}
              className="px-3 py-1 bg-amber-950/20 hover:bg-amber-950/30 rounded text-xs font-semibold transition-colors"
            >
              Choose a plan
            </button>
            <button
              onClick={() => setTrialBannerDismissed(true)}
              className="p-1 hover:bg-amber-950/20 rounded transition-colors"
              aria-label="Dismiss trial banner"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Payment failed: access continues during the grace period while the
          provider retries the card. Admins only; not dismissible. */}
      {billingState?.billingStatus === 'past_due' &&
       billingState.graceUntil &&
       currentUser?.role === 'admin' && (
        <div
          className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white text-sm font-medium px-4 py-2.5 flex items-center justify-between shadow-md"
          style={{
            paddingTop: 'calc(0.625rem + var(--safe-top))',
            paddingLeft: 'calc(1rem + var(--safe-left))',
            paddingRight: 'calc(1rem + var(--safe-right))',
          }}
          role="alert"
        >
          <span>
            Your last payment failed. Update your card by{' '}
            <strong>{new Date(billingState.graceUntil).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}</strong>{' '}
            to keep your team's access.
          </span>
          <button
            onClick={() => setShowOrgServices(true)}
            className="ml-4 flex-shrink-0 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors"
          >
            Update payment
          </button>
        </div>
      )}

      {/* Subscription lapsed overlay — full-screen block when org is suspended/canceled
          and trial has expired. Admin sees reactivate CTA; non-admin sees contact message. */}
      {/* Lead Concierge plan (no map): the org pays for Lead Concierge only, so
          GeoAgent shows where to go instead of a "paused" screen. */}
      {billingState && !showOrgServices && billingState.plan === 'concierge' && billingState.status !== 'active' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/95 p-4" data-testid="concierge-only">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-md w-full p-8 text-center shadow-2xl">
            <h2 className="text-xl font-bold text-gray-50 mb-2">Your plan is Lead Concierge</h2>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              {currentUser?.role === 'admin'
                ? 'Your organization uses Lead Concierge on its own, without the map. Open Lead Concierge, or choose a GeoAgent plan to add the map and daily plans. Your data is kept.'
                : 'Your organization uses Lead Concierge on its own, without the map. Ask your admin if you need GeoAgent.'}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => {
                  window.location.href = await handoffUrl(process.env.REACT_APP_CONCIERGE_URL || 'http://localhost:3002');
                }}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                Open Lead Concierge
              </button>
              {currentUser?.role === 'admin' && (
                <button
                  onClick={() => setShowOrgServices(true)}
                  className="w-full py-2 border border-gray-600 text-gray-200 rounded-lg text-sm hover:bg-gray-800"
                >
                  Add the map
                </button>
              )}
              <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {billingState && !showOrgServices && billingState.plan !== 'concierge' &&
       (billingState.status === 'suspended' || billingState.status === 'canceled') &&
       (billingState.daysLeft === null || billingState.daysLeft <= 0) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/95 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-md w-full p-8 text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-50 mb-2">Your subscription is paused</h2>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              {currentUser?.role === 'admin'
                ? 'We couldn\'t collect payment for your NorthAI subscription, so access is paused. Update your payment method to restore access for your team — your data is safe.'
                : 'Your organization\'s NorthAI subscription is paused. Contact your admin to restore access — your data is safe.'}
            </p>
            {currentUser?.role === 'admin' ? (
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setShowOrgServices(true)}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
                >
                  Update payment
                </button>
                <a
                  href="mailto:support@northai.app"
                  className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                >
                  Contact Support
                </a>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-400">
                  Contact your organization's admin to restore access.
                </div>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* §23: Locked-service banner — appears when any API returned 402
          service_not_entitled. Admins get a "Manage services" CTA that
          opens OrgServices; non-admins get an "Ask your admin" hint. */}
      {lockedService && (
        <div style={{ bottom: 'calc(var(--bottom-nav-height) + 1rem)' }}
          className="fixed right-4 z-40 max-w-sm bg-gray-800 border border-amber-700 rounded-lg shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-900/40 rounded">
              <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-100">
                {lockedService.title}
              </h4>
              <p className="text-xs text-gray-300 mt-1">{lockedService.message}</p>
              <div className="mt-3 flex gap-2">
                {currentUser?.role === 'admin' ? (
                  <button
                    onClick={() => setShowOrgServices(true)}
                    className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                  >
                    Plan & billing
                  </button>
                ) : (
                  <span className="px-3 py-1 text-xs bg-gray-900/50 text-gray-300 rounded">
                    Contact your admin to upgrade
                  </span>
                )}
                <button
                  onClick={() => setLockedService(null)}
                  className="px-3 py-1 text-xs bg-gray-700 text-gray-200 rounded hover:bg-gray-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* AgentBriefing now lives in the left panel slot above — removed old modal */}


      {/* Bottom Navigation - Mobile Only - Hide in Focus Mode */}
      {!isFocusMode && (
        <BottomNav
          currentView={currentView}
          onViewChange={handleViewChange}
          onAIBriefing={handleGetBriefing}
          onMenuOpen={() => setShowMobileNav(true)}
          visitCount={totalVisitCount}
          isBriefingActive={isBriefingOpen}
        />
      )}

      {/* Rejection Modal */}
      {showRejectionModal && entityToReject && (
        <RejectionModal
          entity={entityToReject}
          onReject={handleConfirmRejection}
          onClose={() => {
            setShowRejectionModal(false);
            setEntityToReject(null);
          }}
        />
      )}

      {/* Add Visit Modal — shown when user clicks "Add to Visit List" from LeadDetailPanel */}
      <AddVisitModal
        isOpen={showAddVisitModal}
        entityName={addVisitEntity?.name}
        isLoading={addVisitLoading}
        onClose={() => {
          if (addVisitLoading) return; // prevent close mid-flight
          setShowAddVisitModal(false);
          setAddVisitEntity(null);
        }}
        onConfirm={async ({ scheduled_date, customer_confirmed, scheduled_time, scheduled_end_time }) => {
          if (!addVisitEntity) return;
          setAddVisitLoading(true);
          try {
            await executeAddToVisitList(
              addVisitEntity,
              addVisitEntity._visitReason || 'manual_planning',
              scheduled_date,
              customer_confirmed,
              scheduled_time,
              scheduled_end_time
            );
            setShowAddVisitModal(false);
            setAddVisitEntity(null);
          } finally {
            setAddVisitLoading(false);
          }
        }}
      />
    </div>
  );
}

export default App;
